import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  deleteTeam,
  deleteTeamMember,
  editCategory,
  getAmericanoMatches,
  getGroupMatches,
  getKnockoutMatches,
  getLeagueMatches,
  getMexicanoMatches,
  getTournamentCategoryDetail,
  getTournamentCategoryWinners,
  updatePrizePaymentStatus,
  updateTeamMemberStatus,
  getStageDetails,
  movePlayerToTeam,
} from "../../api/tournaments";
import { getPromoCodesByTournament } from "../../api/promoCodes";
import { IPromoCode } from "../../models/IPromoCode";
import {
  getVotingSessionsByTournament,
  createVotingSession,
  updateVotingSession,
  deleteVotingSession,
  getVotingResults,
} from "../../api/voting";
import {
  CustomFormatBlock,
  ITeamDetail,
  ITournamentCategoryDetail,
  ITournamentCategoryWinnersResponse,
} from "../../models/ITournament";
import {
  ArrowRight,
  BrandTelegram,
  Calendar,
  CloudDownload,
  Config,
  DollarSquare,
  Edit,
  EditOne,
  Envelope,
  Eye,
  GitMerge,
  LetterCSquare,
  List,
  ListNumber,
  Menu as MenuIcon,
  Pencil,
  Plus,
  Question,
  Ribbon,
  Telephone,
  Trash,
  Users,
  X,
} from "@mynaui/icons-react";
import { Copy, Check } from "lucide-react";
import { IPlayerDetail } from "../../models/IPlayer";
import { exportToExcel } from "../../utils/excel";
import { generateBulkVCard } from "../../utils/vcard";
import { formatCategorySchedule } from "../../utils/dates";
import {
  FilterDialog,
  PlayerStatusDialog,
  CreateVotingSessionDialog,
  EditVotingSessionDialog,
  DeleteVotingSessionDialog,
  ConfirmAllTeamMembersDialog,
} from "../../components";
import { Filter } from "../../components/FilterDialog";
import moment from "moment";
import { Trophy, CheckCircle } from "lucide-react";
import DeleteCategoryDialog from "../../components/DeleteCategoryDialog";
import EditCategoryDialog from "../../components/EditCategoryDialog";
import { formatPlannedRounds, getRankName, isAmericanoFormat, playersPerSideFor } from "../../utils/methods";
import { poolPlayers, registrationModeOf, shortTeamLabel } from "../../utils/registrationMode";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  isSuperAdmin,
  canAccessArena,
  canCompleteBracket,
  canCreateSchedule,
  canAccessTeamsTab,
  canAccessPlayersTab,
  canAccessDetailsTab,
  canManageCategories,
} from "../../utils/authorization";
import DeletePlayerDialog from "../../components/DeletePlayerDialog";
import DeleteTeamDialog from "../../components/DeleteTeamDialog";
import CompleteBracketDialog from "../../components/CompleteBracketDialog";
import AddIndividualPlayerDialog from "../../components/AddIndividualPlayerDialog";
import DividePlayersDialog from "../../components/DividePlayersDialog";
import RegisterTeamDialog from "../../components/RegisterTeamDialog";
import { assignAward } from "../../api/players";
import { IMatchDetailDTO } from "../../models/IMatch";
import MatchCard from "../../components/MatchCard";
import { PaymentType, UpdatePrizePaymentRequestDTO, StageDetails } from "../../types/tournament";
import { IVotingSession, IVotingResultsSummary } from "../../models/IVoting";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DescriptionList,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Input,
  Loader,
  Menu,
  PageHeader,
  Segmented,
  Select,
  SearchInput,
  Spinner,
  Tabs,
  Tag,
  Textarea,
  Toolbar,
  TableWrap,
} from "../../ui";
import type { TabOption, TagTone } from "../../ui";
import "./styles.scss";

type CategoryTab = "teams" | "players" | "details" | "arena" | "winners" | "voting";

// The colour class of a prize's trophy: gold, silver, bronze, then muted
const medalClass = (rank: number) => `tcd-medal tcd-medal--${rank >= 1 && rank <= 3 ? rank : "other"}`;

// The old status classes (confirmed / pending / canceled) as Tag tones
const statusTone = (statusClass: string): TagTone =>
  statusClass === "confirmed" ? "ok" : statusClass === "pending" ? "warn" : "danger";

/*
 * A bare row checkbox in the kit's Checkbox look. The kit Checkbox needs a text label, and any text in a cell lands
 * in the Excel export (it reads the table's cells), so this one is named by aria-label instead.
 */
const RowCheck = ({
  checked,
  indeterminate = false,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  label: string;
}) => (
  <label className="ui-check tcd-row-check" onClick={(evt) => evt.stopPropagation()}>
    <input
      className="ui-check__input"
      type="checkbox"
      aria-label={label}
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      onChange={onChange}
    />
    <span className={`ui-check__box${indeterminate ? " is-mixed" : ""}`} aria-hidden="true" />
  </label>
);

const TEAMS_COLUMNS = [
  { label: "name" },
  { label: "captain" },
  { label: "payment status" },
  { label: "# of players" },
  { label: "promo code" },
  { label: "Registration date" },
];

const PLAYERS_COLUMNS = [
  { label: "name" },
  { label: "nationality" },
  { label: "Email" },
  { label: "Phone Number" },
  { label: "registration status" },
  { label: "payment status" },
  { label: "payment method" },
  { label: "Team" },
  { label: "chosen fee" },
  { label: "admin notes" },
];

const getPlayersColumns = (allowIndividualRegistration?: boolean, hasDividedTeams?: boolean) => {
  if (allowIndividualRegistration) {
    const columns = hasDividedTeams ? [...PLAYERS_COLUMNS] : PLAYERS_COLUMNS.filter((col) => col.label !== "Team");
    // Insert gender, promo code, total after nationality
    const nationalityIndex = columns.findIndex((col) => col.label === "nationality");
    if (nationalityIndex !== -1) {
      columns.splice(nationalityIndex + 1, 0, { label: "gender" }, { label: "promo code" }, { label: "total" });
    }
    return columns;
  }
  return PLAYERS_COLUMNS;
};

const getPlayerTotal = (player: IPlayerDetail, promoCodes: IPromoCode[]): string => {
  if (!player.fee) return "-";
  const amount = player.fee.amount;
  if (!player.promoCode) return `${amount} ${player.fee.currency}`;
  const promo = promoCodes.find((p) => p.code === player.promoCode);
  if (!promo) return `${amount} ${player.fee.currency}`;
  if (promo.discountPercentage) {
    return `${Math.max(0, amount - (amount * promo.discountPercentage) / 100)} ${player.fee.currency}`;
  }
  if (promo.discountAmount) {
    return `${Math.max(0, amount - promo.discountAmount)} ${player.fee.currency}`;
  }
  return `${amount} ${player.fee.currency}`;
};

const TournamentCategoryDetail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentUser = useAppSelector((state) => state.auth.currentUser);

  const [searchParams, setSearchParams] = useSearchParams();
  const { id, tournamentCategoryId } = useParams();

  const [matchesLoading, setMatchesLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const [activeMatchTab, setActiveMatchTab] = useState<"upcoming" | "latest">("upcoming");
  const [activeTab, setActiveTab] = useState<"teams" | "players" | "details" | "arena" | "winners" | "voting">("teams");
  const [tournamentCategory, setTournamentCategory] = useState<ITournamentCategoryDetail>();
  const [tournamentPromoCodes, setTournamentPromoCodes] = useState<IPromoCode[]>([]);
  const [winnersData, setWinnersData] = useState<ITournamentCategoryWinnersResponse>();
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [copiedPhoneId, setCopiedPhoneId] = useState<string | null>(null);
  const [paymentUpdateLoading, setPaymentUpdateLoading] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState<{ prizeId: string; teamId: string; isPaid: boolean } | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentType: PaymentType.Cash,
    paymentReference: "",
    notes: "",
  });
  const [winnersFilter, setWinnersFilter] = useState<"all" | "paid" | "unpaid">("all");
  const [bulkPaymentModalOpen, setBulkPaymentModalOpen] = useState(false);
  const [bulkPaymentForm, setBulkPaymentForm] = useState({
    paymentType: PaymentType.BankTransfer,
    paymentReference: "",
    notes: "",
  });
  const [bulkPaymentLoading, setBulkPaymentLoading] = useState(false);
  const [activePlayer, setActivePlayer] = useState<IPlayerDetail>();
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [confirmingAllTeamMembers, setConfirmingAllTeamMembers] = useState(false);
  const [confirmAllMembersDialog, setConfirmAllMembersDialog] = useState(false);
  const [teamToConfirmMembers, setTeamToConfirmMembers] = useState<ITeamDetail | null>(null);
  const [playerFilters, setPlayerFilters] = useState<Filter[]>([]);
  const [teamFilters, setTeamFilters] = useState<Filter[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<IPlayerDetail[]>([]);
  const [filteredTeams, setFilteredTeams] = useState<ITeamDetail[]>([]);
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editMode, setEditMode] = useState<"details" | "fees" | "prizes" | "format" | "team">("details");
  const [completeBracketDialog, setCompleteBracketDialog] = useState(false);
  const [selectedStage, setSelectedStage] = useState<StageDetails | null>(null);
  const [stageDetailsLoading, setStageDetailsLoading] = useState(false);

  const [selectedBlockId, setSelectedBlockId] = useState<string>("");
  const [awardLoading, setAwardLoading] = useState<boolean>(false);
  const [editingAwardId, setEditingAwardId] = useState<string | null>(null);
  const [search, setSearch] = useState<Record<string, string>>({});

  const inputRef = useRef<Record<string, HTMLDivElement | null>>({});
  const [matches, setMatches] = useState<IMatchDetailDTO[]>([]);

  const [deletePlayerDialog, setDeletePlayerDialog] = useState(false);
  const [deleteTeamDialog, setDeleteTeamDialog] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<IPlayerDetail>();
  const [teamToDelete, setTeamToDelete] = useState<ITeamDetail>();
  const [addIndividualPlayerDialog, setAddIndividualPlayerDialog] = useState(false);
  const [dividePlayersDialog, setDividePlayersDialog] = useState(false);
  const [registerTeamDialog, setRegisterTeamDialog] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState<"open" | "closed" | "not-open">("not-open");
  const [playerSearchTerm, setPlayerSearchTerm] = useState("");

  // Check if individual registration has been divided into real teams
  const hasDividedTeams = !!(
    tournamentCategory?.allowIndividualRegistration &&
    tournamentCategory?.teams &&
    (tournamentCategory.teams as any[]).some((t: any) => t.teamName !== "__INDIVIDUAL_REGISTRATION__")
  );

  // Teams + Individuals (spec 2026-10-29): an individual category that also takes complete teams, so it has
  // the team views from the start and solo players can still be waiting in the pool next to premade teams.
  const isBothCategory =
    registrationModeOf(tournamentCategory?.allowIndividualRegistration, tournamentCategory?.allowTeamRegistration) ===
    "both";
  // Divide players only takes players still in the pool.
  const soloPool = poolPlayers(tournamentCategory?.players ?? []);

  // Americano/Mexicano unit teams are auto-created by the backend at schedule
  // generation, so these categories need the Arena tab before any real team exists.
  const isAmericanoCategory =
    isAmericanoFormat(tournamentCategory?.format) ||
    !!tournamentCategory?.customFormatBlocks?.some((b) => isAmericanoFormat(b.blockType));

  // Voting state
  const [votingSessions, setVotingSessions] = useState<IVotingSession[]>([]);
  const [votingLoading, setVotingLoading] = useState(false);
  const [votingResults, setVotingResults] = useState<IVotingResultsSummary | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [createVotingDialog, setCreateVotingDialog] = useState(false);
  const [editVotingDialog, setEditVotingDialog] = useState(false);
  const [deleteVotingDialog, setDeleteVotingDialog] = useState(false);
  const [selectedVotingSession, setSelectedVotingSession] = useState<IVotingSession | null>(null);

  const hasEmptyTeamsInFirstRound = () => {
    const selectedBlock = tournamentCategory?.customFormatBlocks?.find((block) => block.id === selectedBlockId);
    if (selectedBlock?.blockType !== "Knockout" || !matches.length) return false;

    const firstRoundMatches = matches.filter((match) => {
      return !match.parentMatch1Id && !match.parentMatch2Id;
    });

    if (firstRoundMatches.length === 0) return false;

    // Check if ALL first round matches have empty team IDs (not just some)
    // Button should only show when bracket needs to be filled for the first time
    const allEmpty = firstRoundMatches.every((match) => !match.homeTeamId || !match.awayTeamId);

    return allEmpty;
  };

  const handleCompleteBracketClick = async () => {
    if (!id || !selectedBlockId) return;

    // Open dialog immediately with partial data
    const selectedBlock = tournamentCategory?.customFormatBlocks?.find((block) => block.id === selectedBlockId);

    const partialStage: StageDetails = {
      id: selectedBlockId,
      formatName: selectedBlock?.blockType || "Knockout",
      aliasName: selectedBlock?.aliasName || "",
      tournamentName: tournamentCategory?.tournamentName || "",
      categoryName: tournamentCategory?.categoryName || "",
      precedingStages: [],
      teams: [],
      order: 0,
      startDate: "",
      endDate: "",
      knockoutRules: selectedBlock?.knockoutRules || {},
      leagueRules: selectedBlock?.leagueRules || {},
      americanoRules: selectedBlock?.americanoRules,
    };

    setSelectedStage(partialStage);
    setCompleteBracketDialog(true);
    setStageDetailsLoading(true);

    // Fetch the full stage details including precedingStages in the background
    try {
      const { data } = await getStageDetails(id, selectedBlockId);
      setSelectedStage(data);
    } catch (error) {
      console.error("Error fetching stage details:", error);
      dispatch(
        setGeneralAlertMessage({
          message: "Failed to load stage details. Please try again.",
          type: "error",
        }),
      );
      setCompleteBracketDialog(false);
    } finally {
      setStageDetailsLoading(false);
    }
  };

  const parseFiltersFromParams = () => {
    const params = Object.fromEntries(searchParams.entries());

    const nationality = params.nationality?.split(",") || [];
    const captain = params.captain || "";
    const consent = params.consent || "";
    const registrationStatus = params.registrationStatus?.split(",") || [];
    const paymentStatus = params.paymentStatus?.split(",") || [];
    const promoCode = params.promoCode?.split(",") || [];
    const teamPaymentStatus = params.teamPaymentStatus?.split(",") || [];

    return {
      nationality,
      captain,
      consent,
      registrationStatus,
      promoCode,
      paymentStatus,
      teamPaymentStatus,
    };
  };

  const getTournamentData = async () => {
    setLoading(true);
    try {
      const { data } = await getTournamentCategoryDetail(tournamentCategoryId!);
      setTournamentCategory(data);
      document.title = `${data.tournamentName} - ${data.categoryName}`;

      if (id) {
        try {
          const promoRes = await getPromoCodesByTournament(id);
          setTournamentPromoCodes(promoRes.data ?? []);
        } catch {}
      }

      const { captain, consent, nationality, promoCode, registrationStatus, paymentStatus, teamPaymentStatus } =
        parseFiltersFromParams();
      const nationalities = Array.from(new Set((data as ITournamentCategoryDetail).players.map((x) => x.nationality)));
      const promoCodes = Array.from(
        new Set((data as ITournamentCategoryDetail).players.map((x) => x.promoCode).filter(Boolean)),
      ) as string[];

      setRegistrationStatus(data.registrationOpen ? "open" : data.registrationClosed ? "closed" : "not-open");
      setPlayerFilters([
        {
          label: "Nationality",
          showMore: true,
          options: nationalities.map((item: string) => ({
            label: item,
            value: item,
            selected: nationality.includes(item),
          })),
        },
        {
          label: "Captain",
          radio: true,
          options: [
            { label: "Yes", value: "true", selected: captain === "true" },
            { label: "No", value: "false", selected: captain === "false" },
          ],
        },
        {
          label: "Consent",
          radio: true,
          options: [
            { label: "Consented", value: "true", selected: consent === "true" },
            { label: "Not Consented", value: "false", selected: consent === "false" },
          ],
        },
        {
          label: "Registration Status",
          options: [
            { label: "Canceled", value: "Canceled", selected: registrationStatus.includes("Canceled") },
            {
              label: "Pending Invite",
              value: "Pending Invite",
              selected: registrationStatus.includes("Pending Invite"),
            },
            {
              label: "Pending Consent",
              value: "Pending Consent",
              selected: registrationStatus.includes("Pending Consent"),
            },
            {
              label: "Pending Payment",
              value: "Pending Payment",
              selected: registrationStatus.includes("Pending Payment"),
            },
            { label: "Confirmed", value: "Confirmed", selected: registrationStatus.includes("Confirmed") },
          ],
        },
        {
          label: "Payment Status",
          options: [
            { label: "Pending", value: "Pending", selected: registrationStatus.includes("Pending") },
            { label: "Paid", value: "Paid", selected: registrationStatus.includes("Paid") },
            { label: "Not Paid", value: "Not Paid", selected: registrationStatus.includes("Not Paid") },
            {
              label: "Cash Payment On Field",
              value: "Cash Payment On Field",
              selected: registrationStatus.includes("Cash Payment On Field"),
            },
          ],
        },
        {
          label: "Promo Code",
          showMore: true,
          options: promoCodes.map((item: string) => ({
            label: item,
            value: item,
            selected: promoCode.includes(item),
          })),
        },
      ]);

      setTeamFilters([
        {
          label: "Team Payment Status",
          options: [
            { label: "Paid", value: "Paid", selected: teamPaymentStatus.includes("Paid") },
            { label: "Partial", value: "Partial", selected: teamPaymentStatus.includes("Partial") },
            { label: "Pending", value: "Pending", selected: teamPaymentStatus.includes("Pending") },
            { label: "Unpaid", value: "Unpaid", selected: teamPaymentStatus.includes("Unpaid") },
            { label: "N/A", value: "N/A", selected: teamPaymentStatus.includes("N/A") },
          ],
        },
      ]);

      if (data.customFormatBlocks && data.customFormatBlocks.length > 0) {
        const firstBlock: CustomFormatBlock = data.customFormatBlocks.sort(
          (a: CustomFormatBlock, b: CustomFormatBlock) => a.orderIndex - b.orderIndex,
        )[0];

        setSelectedBlockId(firstBlock.id);
      } else {
        setSelectedBlockId("");
      }

      filterData(data, nationality, captain, consent, registrationStatus, promoCode, paymentStatus, teamPaymentStatus);
    } catch (err: any) {
    } finally {
      setLoading(false);
    }
  };

  const getWinnersData = async () => {
    setWinnersLoading(true);
    try {
      const response = await getTournamentCategoryWinners(tournamentCategoryId!);
      console.log("Winners API response:", response);
      setWinnersData(response);
    } catch (err: any) {
      console.error("Error fetching winners:", err);
    } finally {
      setWinnersLoading(false);
    }
  };

  const copyPhoneToClipboard = async (phoneNumber: string, prizeId: string) => {
    try {
      await navigator.clipboard.writeText(phoneNumber);
      setCopiedPhoneId(prizeId);
      setTimeout(() => setCopiedPhoneId(null), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error("Failed to copy phone number:", err);
    }
  };

  const getPaymentTypeDisplay = (paymentType: number | null): string => {
    if (!paymentType) return "";
    switch (paymentType) {
      case PaymentType.BankTransfer:
        return "Bank Transfer";
      case PaymentType.Cheque:
        return "Cheque";
      case PaymentType.Cash:
        return "Cash";
      case PaymentType.Instapay:
        return "Instapay";
      default:
        return "Unknown";
    }
  };

  const getPaymentStatusClass = (paymentStatus: string): string => {
    // "Paid" - All active members have paid → confirmed (green)
    // "Partial" - Some members have paid, others haven't → pending (yellow)
    // "Pending" - All active members have "Pending" payment status → pending (yellow)
    // "Unpaid" - No members have paid → canceled (red/gray)
    // "N/A" - No active members (all canceled) → canceled (gray)
    switch (paymentStatus) {
      case "Paid":
        return "confirmed";
      case "Partial":
      case "Pending":
        return "pending";
      case "Unpaid":
      case "N/A":
        return "canceled";
      default:
        return "pending";
    }
  };

  const getFilteredWinners = () => {
    if (!winnersData?.data?.prizes) return [];

    switch (winnersFilter) {
      case "paid":
        return winnersData.data.prizes.filter((prize) => prize.isPaid);
      case "unpaid":
        return winnersData.data.prizes.filter((prize) => !prize.isPaid);
      default:
        return winnersData.data.prizes;
    }
  };

  const openPaymentModal = (prizeId: string, teamId: string, isPaid: boolean) => {
    setSelectedPrize({ prizeId, teamId, isPaid });

    // Find the prize data to pre-populate the form
    const prizeData = winnersData?.data?.prizes?.find((prize) => prize.id === prizeId);

    setPaymentForm({
      paymentType: prizeData?.paymentType ? (Number(prizeData.paymentType) as PaymentType) : PaymentType.Cash,
      paymentReference: prizeData?.paymentReference || "",
      notes: prizeData?.paymentNotes || "",
    });
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setSelectedPrize(null);
    setPaymentForm({
      paymentType: PaymentType.Cash,
      paymentReference: "",
      notes: "",
    });
  };

  const updatePaymentStatus = async () => {
    if (!selectedPrize) return;

    setPaymentUpdateLoading(selectedPrize.prizeId);
    try {
      // Find the current prize data to determine if it's already paid
      const currentPrize = winnersData?.data?.prizes?.find((prize) => prize.id === selectedPrize.prizeId);
      const isCurrentlyPaid = currentPrize?.isPaid || false;

      const request: UpdatePrizePaymentRequestDTO = {
        tournamentPrizeId: selectedPrize.prizeId,
        teamId: selectedPrize.teamId,
        paymentType: paymentForm.paymentType,
        paymentReference: paymentForm.paymentReference || undefined,
        paymentDate: new Date().toISOString(),
        notes: paymentForm.notes || undefined,
        isPaid: true, // Always set to true when recording/updating payment
      };

      await updatePrizePaymentStatus(request);

      // Refresh winners data to show updated payment status
      await getWinnersData();

      dispatch(
        setGeneralAlertMessage({
          type: "success",
          message: isCurrentlyPaid ? `Payment details updated successfully` : `Payment recorded successfully`,
        }),
      );

      closePaymentModal();
    } catch (err: any) {
      console.error("Error updating payment status:", err);
      dispatch(
        setGeneralAlertMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to update payment status",
        }),
      );
    } finally {
      setPaymentUpdateLoading(null);
    }
  };

  const openBulkPaymentModal = () => {
    setBulkPaymentModalOpen(true);
  };

  const closeBulkPaymentModal = () => {
    setBulkPaymentModalOpen(false);
    setBulkPaymentForm({
      paymentType: PaymentType.BankTransfer,
      paymentReference: "",
      notes: "",
    });
  };

  const updateBulkPaymentStatus = async () => {
    if (!winnersData?.data?.prizes) return;

    setBulkPaymentLoading(true);
    try {
      const unpaidPrizes = winnersData.data.prizes.filter(
        (prize) => !prize.isPaid && prize.winner && prize.winner.prizeAmount > 0,
      );

      if (unpaidPrizes.length === 0) {
        dispatch(
          setGeneralAlertMessage({
            type: "error",
            message: "No unpaid prizes found to update",
          }),
        );
        return;
      }

      // Update all unpaid prizes
      const updatePromises = unpaidPrizes.map((prize) => {
        const request: UpdatePrizePaymentRequestDTO = {
          tournamentPrizeId: prize.id,
          teamId: prize.winner.teamId,
          paymentType: bulkPaymentForm.paymentType,
          paymentReference: bulkPaymentForm.paymentReference || undefined,
          paymentDate: new Date().toISOString(),
          notes: bulkPaymentForm.notes || undefined,
          isPaid: true,
        };
        return updatePrizePaymentStatus(request);
      });

      await Promise.all(updatePromises);
      await getWinnersData();
      closeBulkPaymentModal();

      dispatch(
        setGeneralAlertMessage({
          type: "success",
          message: `Successfully marked ${unpaidPrizes.length} prize(s) as paid`,
        }),
      );
    } catch (error) {
      console.error("Error updating bulk payment status:", error);
      dispatch(
        setGeneralAlertMessage({
          type: "error",
          message: "Failed to update bulk payment status",
        }),
      );
    } finally {
      setBulkPaymentLoading(false);
    }
  };

  const getFormatMatches = async (blockId: string) => {
    setMatchesLoading(true);
    const block = tournamentCategory?.customFormatBlocks.find((block) => block.id === blockId);

    if (!block) return;

    try {
      if (block.blockType === "League") {
        const { data } = await getLeagueMatches(block.id);

        if (data.matches.filter((x: IMatchDetailDTO) => !x.isCompleted).length > 0) {
          setActiveMatchTab("upcoming");
        } else {
          setActiveMatchTab("latest");
        }

        searchParams.set("matchTab", activeMatchTab);
        setSearchParams(searchParams);
        setMatches(data.matches);
      } else if (block.blockType === "Group") {
        const { data } = await getGroupMatches(block.id);

        if (data.matches.filter((x: IMatchDetailDTO) => !x.isCompleted).length > 0) {
          setActiveMatchTab("upcoming");
        } else {
          setActiveMatchTab("latest");
        }

        searchParams.set("matchTab", activeMatchTab);
        setSearchParams(searchParams);
        setMatches(data.matches);
      } else if (block.blockType === "Knockout") {
        const { data } = await getKnockoutMatches(block.id);

        if (data.matches.filter((x: IMatchDetailDTO) => !x.isCompleted).length > 0) {
          setActiveMatchTab("upcoming");
        } else {
          setActiveMatchTab("latest");
        }
        setMatches(data.matches);
      } else if (isAmericanoFormat(block.blockType)) {
        // Roulette is served by the api/Mexicano routes.
        const { data } =
          block.blockType === "Americano" ? await getAmericanoMatches(block.id) : await getMexicanoMatches(block.id);

        if (data.filter((x: IMatchDetailDTO) => !x.isCompleted).length > 0) {
          setActiveMatchTab("upcoming");
        } else {
          setActiveMatchTab("latest");
        }
        setMatches(data);
      }
    } catch (error) {
      console.error("error fetching matches", error);
    } finally {
      setMatchesLoading(false);
    }
  };

  const handleTabClick = (tab: "teams" | "players" | "details" | "arena" | "winners" | "voting") => {
    setActiveTab(tab);

    // Fetch winners data when winners tab is clicked
    if (tab === "winners" && !winnersData) {
      getWinnersData();
    }

    // Fetch voting sessions when voting tab is clicked
    if (tab === "voting" && votingSessions.length === 0) {
      getVotingSessions();
    }

    // const count = searchParams.get("count");

    searchParams.set("tab", tab);
    // searchParams.set("count", `${Number(count) + 1}`);

    setSearchParams(searchParams);
  };

  const handleBack = () => {
    // const count = searchParams.get("count");
    // navigate(-(Number(count) + 1));
    const goTo = searchParams.get("goTo");
    const playerId = searchParams.get("playerId");

    if (goTo === "player" && playerId) {
      navigate(`/dashboard/players/${playerId}`);
    } else if (goTo === "category") {
      navigate(`/dashboard/tournaments/${id}/categories`);
    } else {
      navigate(`/dashboard/tournaments/${id}`);
    }
  };

  const handleCloseDeleteDialog = (update?: boolean) => {
    setDeleteDialog(false);
    if (update) navigate(`/dashboard/tournaments/${id}`);
  };

  const handleCloseDeletePlayerDialog = () => {
    setDeletePlayerDialog(false);
  };

  const handleCloseDeleteTeamDialog = () => {
    setDeleteTeamDialog(false);
  };

  const filterData = (
    data: ITournamentCategoryDetail,
    nationalityFilters: string[],
    captainFilter: string,
    consentFilter: string,
    registrationStatusFilters: string[],
    promoCodeFilters: string[],
    paymentStatusFilters: string[],
    teamPaymentStatusFilters: string[],
    playerSearchTerm?: string,
  ) => {
    if (data?.players) {
      const filteredPlayers = data.players.filter((player) => {
        const matchNationality = nationalityFilters.length === 0 || nationalityFilters.includes(player.nationality);
        const matchCaptain = captainFilter === "" || String(player.isCaptain) === captainFilter;
        const matchConsent = consentFilter === "" || String(player.consent) === consentFilter;
        const matchRegistrationStatus =
          registrationStatusFilters.length === 0 || registrationStatusFilters.includes(player.registrationStatus);
        const matchPaymentStatus =
          paymentStatusFilters.length === 0 || paymentStatusFilters.includes(player.paymentStatus);
        const matchPromoCode =
          promoCodeFilters.length === 0 || (player.promoCode && promoCodeFilters.includes(player.promoCode));

        // Search functionality
        const searchLower = (playerSearchTerm || "")?.toLowerCase();
        const matchSearch =
          !playerSearchTerm ||
          player.firstName?.toLowerCase().includes(searchLower) ||
          player.lastName?.toLowerCase().includes(searchLower) ||
          player.email?.toLowerCase().includes(searchLower) ||
          (player.phoneNumber && player.phoneNumber?.toLowerCase().includes(searchLower)) ||
          player.teamName?.toLowerCase().includes(searchLower);

        return (
          matchNationality &&
          matchCaptain &&
          matchConsent &&
          matchRegistrationStatus &&
          matchPaymentStatus &&
          matchPromoCode &&
          matchSearch
        );
      });

      setFilteredPlayers(filteredPlayers);
    }

    if (data?.teams) {
      const filteredTeams = data.teams.filter((team) => {
        // Hide __INDIVIDUAL_REGISTRATION__ team from teams list
        if ((team as any).teamName === "__INDIVIDUAL_REGISTRATION__") return false;
        const matchPromo = promoCodeFilters.length === 0 || promoCodeFilters.includes(team.promoCode);
        const matchTeamPaymentStatus =
          teamPaymentStatusFilters.length === 0 || teamPaymentStatusFilters.includes(team.paymentStatus);

        return matchPromo && matchTeamPaymentStatus;
      });

      setFilteredTeams(filteredTeams);
    }
  };

  const handlePlayerSearch = (searchTerm: string) => {
    setPlayerSearchTerm(searchTerm);
    if (tournamentCategory) {
      const { captain, consent, nationality, promoCode, registrationStatus, paymentStatus, teamPaymentStatus } =
        parseFiltersFromParams();
      filterData(
        tournamentCategory,
        nationality,
        captain,
        consent,
        registrationStatus,
        promoCode,
        paymentStatus,
        teamPaymentStatus,
        searchTerm,
      );
    }
  };

  const handleFilterApply = (newFilters: Filter[]) => {
    if (activeTab === "players") setPlayerFilters(newFilters);
    if (activeTab === "teams") setTeamFilters(newFilters);

    const appliedFilters = newFilters.filter((x) => x.options?.some((o) => o.selected));

    // Player Filters
    let nationalityFilters: string[] = [];
    let captainFilter = "";
    let consentFilter = "";
    let registrationStatusFilters: string[] = [];
    let paymentStatusFilters: string[] = [];
    let promoCodeFilters: string[] = [];

    // Team Filters
    let teamPaymentStatusFilters: string[] = [];

    appliedFilters.forEach((filter) => {
      const selectedValues = filter.options ? filter.options.filter((x) => x.selected).map((x) => x.value) : [];

      switch (filter.label) {
        case "Nationality":
          nationalityFilters = selectedValues;
          break;
        case "Captain":
          captainFilter = selectedValues[0] || "";
          break;
        case "Consent":
          consentFilter = selectedValues[0] || "";
          break;
        case "Registration Status":
          registrationStatusFilters = selectedValues;
          break;
        case "Payment Status":
          paymentStatusFilters = selectedValues;
          break;
        case "Promo Code":
          promoCodeFilters = selectedValues;
          break;
        case "Team Payment Status":
          teamPaymentStatusFilters = selectedValues;
          break;
      }
    });

    // const count = searchParams.get("count");
    if (nationalityFilters.length > 0) searchParams.set("nationality", nationalityFilters.join(","));
    if (captainFilter) searchParams.set("captain", captainFilter);
    if (consentFilter) searchParams.set("consent", consentFilter);
    if (registrationStatusFilters.length > 0)
      searchParams.set("registrationStatus", registrationStatusFilters.join(","));
    if (paymentStatusFilters.length > 0) searchParams.set("paymentStatus", paymentStatusFilters.join(","));
    if (promoCodeFilters.length > 0) searchParams.set("promoCode", promoCodeFilters.join(","));
    if (teamPaymentStatusFilters.length > 0) searchParams.set("teamPaymentStatus", teamPaymentStatusFilters.join(","));

    if (activeTab === "players") {
      if (nationalityFilters.length === 0) searchParams.delete("nationality");
      if (!captainFilter) searchParams.delete("captain");
      if (!consentFilter) searchParams.delete("consent");
      if (registrationStatusFilters.length === 0) searchParams.delete("registrationStatus");
      if (paymentStatusFilters.length === 0) searchParams.delete("paymentStatus");
      if (promoCodeFilters.length === 0) searchParams.delete("promoCode");
    }

    if (activeTab === "teams") {
      if (teamPaymentStatusFilters.length === 0) searchParams.delete("teamPaymentStatus");
    }

    // searchParams.set("count", `${Number(count) + 1}`);

    setSearchParams(searchParams);

    if (tournamentCategory)
      filterData(
        tournamentCategory,
        nationalityFilters,
        captainFilter,
        consentFilter,
        registrationStatusFilters,
        promoCodeFilters,
        paymentStatusFilters,
        teamPaymentStatusFilters,
        activeTab === "players" ? playerSearchTerm : "",
      );
  };

  const toggleCheckAll = () => {
    if (filteredPlayers) {
      if (checkedItems.length === Number(filteredPlayers.length)) setCheckedItems([]);
      else if (checkedItems.length < Number(filteredPlayers.length)) setCheckedItems(filteredPlayers.map((x) => x.id));
    }
  };

  const toggleCheck = (id: string) => {
    setCheckedItems((prev) => {
      let updatedItems = [...prev];
      const itemId = updatedItems.findIndex((x) => x === id);

      if (itemId !== -1) {
        updatedItems = updatedItems.filter((x) => x !== id);
      } else updatedItems.push(id);

      return updatedItems;
    });
  };

  const handleBulkConfirm = async () => {
    setLoading(true);
    try {
      const checkedPlayers = tournamentCategory?.players.filter((x) => checkedItems.includes(x.id));

      if (checkedPlayers && checkedPlayers.length > 0) {
        Promise.allSettled(
          checkedPlayers.map((player, id) =>
            updateTeamMemberStatus(player.teamId, player.teamMemberId, { status: "Confirmed", paymentStatus: "Paid" }),
          ),
        );
      }
      getTournamentData();
    } catch (error) {
      console.error("Error confirming player selection");
    } finally {
      setCheckedItems([]);
    }
  };

  const handleConfirmAllTeamMembers = async () => {
    if (!teamToConfirmMembers) return;

    setConfirmingAllTeamMembers(true);
    try {
      // Confirm all members of this specific team
      const results = await Promise.allSettled(
        teamToConfirmMembers.teamMembers.map((member) =>
          updateTeamMemberStatus(teamToConfirmMembers.id, member.teamMemberId, {
            status: "Confirmed",
            paymentStatus: "Paid",
          }),
        ),
      );

      // Count successes and failures
      const successCount = results.filter((r) => r.status === "fulfilled").length;
      const failureCount = results.filter((r) => r.status === "rejected").length;

      if (failureCount > 0) {
        dispatch(
          setGeneralAlertMessage({
            message: `Confirmed ${successCount} team members. ${failureCount} failed.`,
            type: "error",
          }),
        );
      } else {
        dispatch(
          setGeneralAlertMessage({
            message: `Successfully confirmed all ${successCount} members of team "${teamToConfirmMembers.teamName}"!`,
            type: "success",
          }),
        );
      }

      // Refresh the data
      getTournamentData();
    } catch (error) {
      console.error("Error confirming all team members:", error);
      dispatch(
        setGeneralAlertMessage({
          message: "Failed to confirm team members. Please try again.",
          type: "error",
        }),
      );
    } finally {
      setConfirmingAllTeamMembers(false);
    }
  };

  const openContacts = () => {
    const checkedPlayers = tournamentCategory?.players.filter((x) => checkedItems.includes(x.id));

    if (!checkedPlayers || checkedPlayers.length === 0) return;

    const contacts = checkedPlayers.map((player) => ({
      name: `${player.firstName.charAt(0).toUpperCase()}${player.firstName.slice(1)} ${player?.lastName
        ?.charAt(0)
        .toUpperCase()}${player.lastName?.slice(1)}`.trim(),
      phone: player.phoneNumber || "",
      email: player.email || undefined,
      tournament: tournamentCategory?.tournamentName || "",
      category: tournamentCategory?.categoryName || "",
    }));

    const vcard = generateBulkVCard(contacts);
    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);

    // Open in a new tab (mobile will usually trigger "Add to Contacts")
    window.open(url, "_blank");
  };

  const addPlayerToContacts = (player: IPlayerDetail) => {
    const contact = {
      name: `${player.firstName.charAt(0).toUpperCase()}${player.firstName.slice(1)} ${player?.lastName
        ?.charAt(0)
        .toUpperCase()}${player.lastName?.slice(1)}`.trim(),
      phone: player.phoneNumber || "",
      email: player.email || undefined,
      tournament: tournamentCategory?.tournamentName || "",
      category: tournamentCategory?.categoryName || "",
    };

    const vcard = generateBulkVCard([contact]);
    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);

    // Open in a new tab (mobile will usually trigger "Add to Contacts")
    window.open(url, "_blank");
  };

  const handleCancel = () => {
    setCheckedItems([]);
  };

  const handleDeleteTeamMember = async () => {
    setLoading(true);
    try {
      const res = await deleteTeamMember(playerToDelete?.teamMemberId!);
      if (res.data) {
        getTournamentData();
        dispatch(setGeneralAlertMessage({ message: res.message, type: "success" }));
      }
    } catch (error: any) {
      console.error("team member deletion failed", error.message);
      dispatch(setGeneralAlertMessage({ message: error.message, type: "error" }));
      setLoading(false);
    }
  };

  const handleDeleteTeam = async () => {
    setLoading(true);
    try {
      const res = await deleteTeam(teamToDelete?.id!);
      if (res.data) {
        getTournamentData();
        dispatch(setGeneralAlertMessage({ message: res.message, type: "success" }));
      }
    } catch (error: any) {
      console.error("team deletion failed", error.message);
      dispatch(setGeneralAlertMessage({ message: error.message, type: "error" }));
      setLoading(false);
    }
  };

  const assignAwardHandler = async (playerId: string, awardId: string) => {
    setAwardLoading(true);
    try {
      await assignAward({ playerId, awardId });
    } catch (error) {
      console.error("error assigning award to player");
    } finally {
      setAwardLoading(false);
    }
  };

  const updateStatus = async (value: "open" | "closed" | "not-open") => {
    try {
      const res = await editCategory(tournamentCategory?.id!, {
        registrationClosed: value === "closed" ? true : false,
        registrationOpen: value === "open" ? true : false,
        arenaPublished: tournamentCategory?.arenaPublished,
      });
      setRegistrationStatus(value);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const updatePublishArena = async () => {
    try {
      const res = await editCategory(tournamentCategory?.id!, {
        arenaPublished: !tournamentCategory?.arenaPublished,
      });
      setTournamentCategory({ ...tournamentCategory!, arenaPublished: !tournamentCategory?.arenaPublished });
      dispatch(setGeneralAlertMessage({ message: "Arena published successfully", type: "success" }));
    } catch (error: any) {
      console.error(error.message);
      dispatch(setGeneralAlertMessage({ message: error.message, type: "error" }));
    }
  };

  // Voting functions
  const getVotingSessions = async () => {
    setVotingLoading(true);
    try {
      const { data } = await getVotingSessionsByTournament(id!);
      // Filter to only show sessions for this specific category
      const categorySessions = data.filter((session) => session.tournamentCategoryId === tournamentCategoryId);
      setVotingSessions(categorySessions);

      // If there's a voting session, fetch its results
      if (categorySessions.length > 0) {
        await getVotingResultsForSession(categorySessions[0].id);
      }
    } catch (error) {
      console.error("Error fetching voting sessions:", error);
    } finally {
      setVotingLoading(false);
    }
  };

  const getVotingResultsForSession = async (sessionId: string) => {
    setResultsLoading(true);
    try {
      const { data } = await getVotingResults(sessionId);
      setVotingResults(data);
    } catch (error) {
      console.error("Error fetching voting results:", error);
      setVotingResults(null);
    } finally {
      setResultsLoading(false);
    }
  };

  const handleCreateVotingSession = () => {
    if (votingSessions.length > 0) {
      dispatch(
        setGeneralAlertMessage({
          message: "Only one voting session is allowed per category",
          type: "error",
        }),
      );
      return;
    }
    setCreateVotingDialog(true);
  };

  const handleEditVotingSession = (session: IVotingSession) => {
    setSelectedVotingSession(session);
    setEditVotingDialog(true);
  };

  const handleDeleteVotingSession = (session: IVotingSession) => {
    setSelectedVotingSession(session);
    setDeleteVotingDialog(true);
  };

  const handleToggleVotingSession = async (session: IVotingSession) => {
    try {
      await updateVotingSession(session.id, { isOpen: !session.isOpen });
      // Refresh voting sessions
      await getVotingSessions();
      dispatch(
        setGeneralAlertMessage({
          message: `Voting session ${session.isOpen ? "closed" : "opened"} successfully`,
          type: "success",
        }),
      );
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error.message, type: "error" }));
    }
  };

  const handleAssignAwards = async () => {
    if (!votingResults || votingResults.results.length === 0) {
      dispatch(setGeneralAlertMessage({ message: "No voting results to assign", type: "error" }));
      return;
    }

    try {
      setResultsLoading(true);

      // Group results by award and get the winner (highest vote count) for each award
      const awardWinners = votingResults.results.reduce((acc, result) => {
        const existingAward = acc.find((award) => award.awardId === result.awardId);
        if (existingAward) {
          // Keep the one with higher vote count
          if (result.voteCount > existingAward.voteCount) {
            existingAward.votedPlayerId = result.votedPlayerId;
            existingAward.votedPlayerName = result.votedPlayerName;
            existingAward.voteCount = result.voteCount;
          }
        } else {
          acc.push({
            awardId: result.awardId,
            awardName: result.awardName,
            votedPlayerId: result.votedPlayerId,
            votedPlayerName: result.votedPlayerName,
            voteCount: result.voteCount,
          });
        }
        return acc;
      }, [] as any[]);

      // Assign awards to winners
      const assignmentPromises = awardWinners.map((winner) =>
        assignAward({
          playerId: winner.votedPlayerId,
          awardId: winner.awardId,
        }),
      );

      await Promise.all(assignmentPromises);

      dispatch(
        setGeneralAlertMessage({
          message: `Successfully assigned ${awardWinners.length} awards to winners`,
          type: "success",
        }),
      );

      // Refresh the data to show updated awards
      await getTournamentData();
    } catch (error: any) {
      console.error("Error assigning awards:", error);
      dispatch(
        setGeneralAlertMessage({
          message: error.message || "Failed to assign awards",
          type: "error",
        }),
      );
    } finally {
      setResultsLoading(false);
    }
  };

  useEffect(() => {
    getTournamentData();

    const tab = searchParams.get("tab") as "teams" | "players" | "details" | "arena" | "winners" | "voting" | null;
    if (tab) {
      setActiveTab(tab);
      if (tab === "winners") getWinnersData();
      if (tab === "voting") getVotingSessions();
    }
    // Don't set default here — wait for data to load, second useEffect handles it

    const matchTab = searchParams.get("matchTab") as "upcoming" | "latest" | null;
    if (matchTab) setActiveMatchTab(matchTab);

    const selectedBlockId = searchParams.get("selectedBlockId") as string | null;
    if (selectedBlockId) setSelectedBlockId(selectedBlockId);
  }, []);

  useEffect(() => {
    if (selectedBlockId) getFormatMatches(selectedBlockId);
    else setMatches([]);
  }, [selectedBlockId]);

  // All tab redirection is handled by the useEffect below once data is fully loaded
  const initialTabApplied = useRef(false);
  useEffect(() => {
    if (!tournamentCategory || loading) return;

    const isIndividualNotDivided =
      tournamentCategory.allowIndividualRegistration && !hasDividedTeams && !isAmericanoCategory && !isBothCategory;
    const urlTab = searchParams.get("tab");

    // Helper: is the given tab valid for the current state?
    const isTabValid = (tab: string) => {
      if (tab === "teams" && isIndividualNotDivided) return false;
      if (tab === "arena" && (isIndividualNotDivided || !canAccessArena(currentUser))) return false;
      if (tab === "winners" && (!isSuperAdmin(currentUser) || !tournamentCategory.prizes?.length)) return false;
      if (tab === "voting" && !isSuperAdmin(currentUser)) return false;
      if (tab === "players" && !canAccessPlayersTab(currentUser)) return false;
      if (tab === "teams" && !canAccessTeamsTab(currentUser)) return false;
      if (tab === "details" && !canAccessDetailsTab(currentUser)) return false;
      return true;
    };

    // Helper: get the best default tab
    const getDefaultTab = () => {
      if (isIndividualNotDivided) return "players";
      if (canAccessArena(currentUser)) return "arena";
      return "teams";
    };

    // On first load or if current tab is invalid, redirect
    if (!initialTabApplied.current || !isTabValid(activeTab)) {
      const targetTab = urlTab && isTabValid(urlTab) ? urlTab : getDefaultTab();
      if (activeTab !== targetTab) {
        setActiveTab(targetTab as any);
        searchParams.set("tab", targetTab);
        setSearchParams(searchParams);
      }
      initialTabApplied.current = true;
    }
  }, [tournamentCategory, hasDividedTeams, loading]);

  useEffect(() => {
    const { captain, consent, nationality, promoCode, registrationStatus, paymentStatus, teamPaymentStatus } =
      parseFiltersFromParams();

    filterData(
      tournamentCategory!,
      nationality,
      captain,
      consent,
      registrationStatus,
      promoCode,
      paymentStatus,
      teamPaymentStatus,
      activeTab === "players" ? playerSearchTerm : "",
    );
  }, [activeTab, playerSearchTerm]);

  const playerFiltersApplied = useMemo(
    () => playerFilters.filter((x) => x.options?.some((o) => o.selected)).length,
    [playerFilters],
  );

  const teamFiltersApplied = useMemo(
    () => teamFilters.filter((x) => x.options?.some((o) => o.selected)).length,
    [teamFilters],
  );

  // Extract unique custom field columns from players data
  const customFieldColumns = useMemo(() => {
    if (!tournamentCategory?.players) return [];

    const fieldsMap = new Map<string, { fieldKey: string; fieldName: string; displayOrder: number }>();

    tournamentCategory.players.forEach((player) => {
      player.customFieldValues?.forEach((fieldValue) => {
        if (!fieldsMap.has(fieldValue.fieldKey)) {
          fieldsMap.set(fieldValue.fieldKey, {
            fieldKey: fieldValue.fieldKey,
            fieldName: fieldValue.fieldName,
            displayOrder: 0, // Default order, could be enhanced if API provides it
          });
        }
      });
    });

    return Array.from(fieldsMap.values());
  }, [tournamentCategory?.players]);

  // Helper function to get custom field value for a player
  const getPlayerCustomFieldValue = (player: IPlayerDetail, fieldKey: string): string => {
    const fieldValue = player.customFieldValues?.find((fv) => fv.fieldKey === fieldKey);
    return fieldValue?.value || "-";
  };

  const isIndividual = tournamentCategory?.allowIndividualRegistration;
  const confirmedPlayers = tournamentCategory?.players.filter((x) => x.registrationStatus === "Confirmed").length;

  // The tabs, in the order and under the permissions they always had
  const tabOptions: TabOption<CategoryTab>[] = [];
  const hasTeamViews = isIndividual !== true || hasDividedTeams || isBothCategory;
  if (canAccessArena(currentUser) && (hasTeamViews || isAmericanoCategory))
    tabOptions.push({ value: "arena", label: "Arena" });
  if (canAccessTeamsTab(currentUser) && hasTeamViews)
    tabOptions.push({ value: "teams", label: "Teams", count: filteredTeams.length > 0 ? filteredTeams.length : undefined });
  if (canAccessPlayersTab(currentUser))
    tabOptions.push({
      value: "players",
      label: "Players",
      count: filteredPlayers.length > 0 ? filteredPlayers.length : undefined,
    });
  if (canAccessDetailsTab(currentUser)) tabOptions.push({ value: "details", label: "Category Details" });
  if (isSuperAdmin(currentUser)) tabOptions.push({ value: "voting", label: "Voting" });
  if (isSuperAdmin(currentUser) && tournamentCategory?.prizes && tournamentCategory.prizes.length > 0)
    tabOptions.push({ value: "winners", label: "Winners" });

  // The stage picked in the Arena tab
  const selectedBlock = tournamentCategory?.customFormatBlocks?.find((block) => block.id === selectedBlockId);

  const registrationTone: TagTone =
    registrationStatus === "open" ? "ok" : registrationStatus === "closed" ? "danger" : "neutral";

  return (
    <div className="tournament-category-detail">
      {loading ? (
        <Loader />
      ) : (
        <>
          <PageHeader
            eyebrow={tournamentCategory?.tournamentName}
            back={{ onClick: handleBack, label: "Back" }}
            title={tournamentCategory?.categoryName}
            subtitle={
              <span className="tcd-header__meta">
                {isIndividual && (
                  <Tag tone="brand">{isBothCategory ? "Teams + Individuals" : "Individual Registration"}</Tag>
                )}
                {isIndividual ? (
                  <span>
                    {tournamentCategory?.players.length} Players ({confirmedPlayers} Confirmed)
                  </span>
                ) : (
                  <>
                    <span>
                      {tournamentCategory?.teams.length} Teams (
                      {tournamentCategory?.teams.filter((x) => x.status === "Confirmed").length} Confirmed)
                    </span>
                    <span className="tcd-header__dot" aria-hidden="true" />
                    <span>
                      {tournamentCategory?.players.length} Players ({confirmedPlayers} Confirmed)
                    </span>
                  </>
                )}
              </span>
            }
            actions={
              <>
                {canManageCategories(currentUser) ? (
                  <Select
                    value={registrationStatus}
                    onChange={(e) => updateStatus(e.target.value as "open" | "closed" | "not-open")}
                    className={`tcd-reg-status tcd-reg-status--${registrationStatus}`}
                    aria-label="Registration status"
                  >
                    <option value={"open"}>Registration Open</option>
                    <option value={"closed"}>Registration Closed</option>
                    <option value={"not-open"}>Registration Not Open</option>
                  </Select>
                ) : (
                  <Tag tone={registrationTone} size="md">
                    {registrationStatus === "open" && "Registration Open"}
                    {registrationStatus === "closed" && "Registration Closed"}
                    {registrationStatus === "not-open" && "Registration Not Open"}
                  </Tag>
                )}
                {canManageCategories(currentUser) &&
                  ((activeTab === "teams" &&
                    !tournamentCategory?.allowIndividualRegistration &&
                    (tournamentCategory?.teams as ITeamDetail[])?.length > 0) ||
                    (activeTab === "players" && (tournamentCategory?.players as IPlayerDetail[])?.length > 0)) && (
                    <Button
                      icon={<CloudDownload size={18} />}
                      onClick={() =>
                        exportToExcel(
                          activeTab === "teams" ? "teams-table" : activeTab === "players" ? "players-table" : "",
                          activeTab === "teams" ? "Teams" : activeTab === "players" ? "Players" : "",
                        )
                      }
                    >
                      Export
                    </Button>
                  )}
                {canManageCategories(currentUser) && (
                  <Button variant="ghost" icon={<Trophy size={16} />} onClick={() => updatePublishArena()}>
                    {tournamentCategory?.arenaPublished ? "Unpublish Arena" : "Publish Arena"}
                  </Button>
                )}
                {canManageCategories(currentUser) && (
                  <Menu
                    label="Edit category"
                    trigger={(menu) => (
                      <Button
                        ref={menu.ref}
                        variant="ghost"
                        icon={<EditOne size={18} />}
                        data-tooltip="Edit category"
                        onClick={menu.onClick}
                        aria-expanded={menu["aria-expanded"]}
                        aria-haspopup={menu["aria-haspopup"]}
                      >
                        Edit
                      </Button>
                    )}
                    items={[
                      {
                        label: "Edit details",
                        icon: <List size={16} />,
                        onClick: () => {
                          setEditDialog(true);
                          setEditMode("details");
                        },
                      },
                      {
                        label: "Edit Format & Rules",
                        icon: <GitMerge size={16} />,
                        onClick: () => {
                          setEditDialog(true);
                          setEditMode("format");
                        },
                      },
                      {
                        label: "Team & Player Settings",
                        icon: <Users size={16} />,
                        onClick: () => {
                          setEditDialog(true);
                          setEditMode("team");
                        },
                      },
                      {
                        label: "Edit Prizes & Awards",
                        icon: <Ribbon size={16} />,
                        onClick: () => {
                          setEditDialog(true);
                          setEditMode("prizes");
                        },
                      },
                      {
                        label: "Edit Fees",
                        icon: <DollarSquare size={16} />,
                        onClick: () => {
                          setEditDialog(true);
                          setEditMode("fees");
                        },
                      },
                    ]}
                  />
                )}
                {canManageCategories(currentUser) && (
                  <Menu
                    label="More options"
                    className="tcd-header__more"
                    items={[
                      {
                        label: "Delete category",
                        icon: <Trash size={16} />,
                        tone: "danger",
                        onClick: () => setDeleteDialog(true),
                      },
                    ]}
                  />
                )}
              </>
            }
          >
            <div className="tcd-info">
              <span className="tcd-info__item">
                {tournamentCategory?.format === "League" ? (
                  <ListNumber size={20} />
                ) : tournamentCategory?.format === "Knockout" ? (
                  <GitMerge size={20} />
                ) : tournamentCategory?.format === "League-Knockout" ? (
                  <Ribbon size={20} />
                ) : tournamentCategory?.format === "Groups-Knockout" ? (
                  <MenuIcon size={20} />
                ) : (
                  <Question size={20} />
                )}
                {tournamentCategory?.format.replace("-", " + ")}
              </span>
              <span className="tcd-info__item">
                <Users size={20} />
                Max. {tournamentCategory?.maxPlayers === 100 ? "Unlimited" : tournamentCategory?.maxPlayers} players per
                team
              </span>
              {tournamentCategory?.startDate && (
                <span className="tcd-info__item">
                  <Calendar size={20} />
                  {formatCategorySchedule(tournamentCategory.startDate, tournamentCategory.endDate)}
                </span>
              )}
            </div>
            <Tabs
              className="tcd-tabs"
              ariaLabel="Category sections"
              options={tabOptions}
              value={activeTab}
              onChange={(tab) => handleTabClick(tab)}
            />
          </PageHeader>
          <div className="tcd-content">
              {activeTab === "details" && canAccessDetailsTab(currentUser) && (
                <div className="tcd-details">
                  <Card className="tcd-details__wide">
                    <DescriptionList
                      columns={3}
                      mobileColumns={2}
                      items={[
                        { label: "Format", value: tournamentCategory?.format.replace("-", " + ") },
                        { label: "Included in Ranking", value: tournamentCategory?.isIncludedInRanking ? "Yes" : "No" },
                        ...(tournamentCategory?.format.includes("League") || tournamentCategory?.format.includes("Groups")
                          ? [
                              {
                                label: "Encounters",
                                value:
                                  tournamentCategory.leagueRules.length === 1
                                    ? tournamentCategory.leagueRules[0].encounters
                                    : "-",
                              },
                              {
                                label: "Points for Win",
                                value:
                                  tournamentCategory.leagueRules.length === 1
                                    ? tournamentCategory.leagueRules[0].pointsForWin
                                    : "-",
                              },
                              {
                                label: "Ranking Criteria",
                                value:
                                  tournamentCategory.leagueRules.length === 1
                                    ? tournamentCategory.leagueRules[0].criteria == "total"
                                      ? "Total Points"
                                      : "Direct Matches"
                                    : "-",
                              },
                            ]
                          : []),
                        ...(tournamentCategory?.format.includes("Knockout")
                          ? [
                              {
                                label: "Mode",
                                value:
                                  tournamentCategory.knockoutRules.length === 1
                                    ? tournamentCategory.knockoutRules[0].mode === "single"
                                      ? "Single Elimination"
                                      : "Double Elimination"
                                    : "-",
                              },
                              {
                                label: "3rd Place Match",
                                value:
                                  tournamentCategory.knockoutRules.length === 1
                                    ? tournamentCategory.knockoutRules[0].hasThirdPlaceMatch
                                      ? "Yes"
                                      : "No"
                                    : "-",
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Card>
                  <Card className="tcd-details__wide">
                    <DescriptionList
                      columns={3}
                      mobileColumns={2}
                      items={[
                        ...(isBothCategory
                          ? [
                              { label: "Players per team", value: tournamentCategory?.teamSize ?? "-" },
                              {
                                label: "Min. players per team",
                                value: tournamentCategory?.teamMinSize ?? tournamentCategory?.teamSize ?? "-",
                              },
                              // The per-team mix only applies to a mixed category.
                              ...(tournamentCategory &&
                              tournamentCategory.teamRequirements.maxMen > 0 &&
                              tournamentCategory.teamRequirements.maxWomen > 0
                                ? [
                                    {
                                      label: "Max # of Males per Team",
                                      value: tournamentCategory.teamMaxMen ?? "No limit",
                                    },
                                    {
                                      label: "Max # of Females per Team",
                                      value: tournamentCategory.teamMaxWomen ?? "No limit",
                                    },
                                  ]
                                : []),
                            ]
                          : [
                              {
                                label: "Max players per team",
                                value:
                                  tournamentCategory?.maxPlayers === 100 ? "Unlimited" : tournamentCategory?.maxPlayers,
                              },
                              { label: "Min # of Males per Team", value: tournamentCategory?.teamRequirements.minMen },
                              {
                                label: "Max # of Males per Team",
                                value:
                                  tournamentCategory?.teamRequirements.maxMen === 100
                                    ? "Unlimited"
                                    : tournamentCategory?.teamRequirements.maxMen,
                              },
                              {
                                label: "Min # of Females per Team",
                                value: tournamentCategory?.teamRequirements.minWomen,
                              },
                              {
                                label: "Max # of Females per Team",
                                value:
                                  tournamentCategory?.teamRequirements.maxWomen === 100
                                    ? "Unlimited"
                                    : tournamentCategory?.teamRequirements.maxWomen,
                              },
                            ]),
                        ...((tournamentCategory?.femaleBonusPoints !== undefined &&
                          tournamentCategory?.femaleBonusPoints > 0) ||
                        (tournamentCategory?.femaleBonusCap !== undefined && tournamentCategory?.femaleBonusCap > 0)
                          ? [
                              { label: "Female Bonus Points", value: tournamentCategory?.femaleBonusPoints ?? 0 },
                              { label: "Female Bonus Cap", value: tournamentCategory?.femaleBonusCap ?? 0 },
                            ]
                          : []),
                      ]}
                    />
                  </Card>
                  {tournamentCategory?.prizes.length ? (
                    <Card title="Prizes">
                      <div className="tcd-prizes">
                        {tournamentCategory?.prizes
                          .sort((a, b) => (a.rank > b.rank ? 1 : -1))
                          .map((prize, id) => {
                            console.log("Tournament Category Prize data:", prize);
                            return (
                              <div className="tcd-prize" key={id}>
                                <Trophy size={24} className={medalClass(prize.rank)} />
                                <div className="tcd-prize__data">
                                  <p className="tcd-prize__rank">{getRankName(prize.rank)} Place Prize</p>
                                  <p className="tcd-prize__value">
                                    {prize?.gift
                                      ? prize?.gift
                                      : `${prize?.amount?.toLocaleString()} ${prize?.currency}`}
                                    {prize.rankingPoints ? (
                                      <Tag tone="brand">{prize.rankingPoints} Ranking Points</Tag>
                                    ) : null}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </Card>
                  ) : null}
                  {tournamentCategory?.fees.length ? (
                    <Card title="Fees">
                      <DescriptionList
                        columns={1}
                        items={tournamentCategory.fees.map((fee) => ({
                          label: `${fee.description} (${fee.perTeam ? "Per Team" : "Per Player"})`,
                          value: `${fee?.amount?.toLocaleString() ?? ""} ${fee?.currency ?? ""}`,
                        }))}
                      />
                    </Card>
                  ) : null}
                  {canManageCategories(currentUser) && (
                    <Card
                      title="Custom Registration Fields"
                      subtitle="Collect additional information from players during registration, such as passport numbers, full names, dates of birth, etc."
                    >
                      <Button
                        variant="tint"
                        icon={<Config size={18} />}
                        onClick={() => navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}/custom-fields`)}
                      >
                        Manage Custom Fields
                      </Button>
                    </Card>
                  )}
                  {tournamentCategory?.awards.length ? (
                    <Card title="Awards">
                      {awardLoading ? (
                        <div className="tcd-awards__loading">
                          <Spinner />
                        </div>
                      ) : (
                        <div className="tcd-awards">
                          {tournamentCategory.awards.map((award) => {
                            const originalName = award.playerName.trim();
                            const searchValue = search[award.id] || "";
                            const isEditing = editingAwardId === award.id;
                            const hasTyped = searchValue !== originalName;
                            const filteredPlayers = tournamentCategory.players.filter((p) =>
                              `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchValue.toLowerCase()),
                            );

                            return (
                              <div className="tcd-award" key={award.id}>
                                <Ribbon size={20} className="tcd-award__icon" />
                                <div className="tcd-award__content" ref={(el) => (inputRef.current[award.id] = el)}>
                                  <div className="tcd-award__main">
                                    <p className="tcd-award__name">{award.name}</p>
                                    {isEditing && !awardLoading ? (
                                      <div className="tcd-award__picker">
                                        <Input
                                          inputSize="sm"
                                          type="text"
                                          placeholder="Search player"
                                          value={searchValue}
                                          autoFocus
                                          onChange={(e) =>
                                            setSearch((prev) => ({ ...prev, [award.id]: e.target.value }))
                                          }
                                        />
                                        {hasTyped && searchValue && (
                                          <div className="tcd-award__suggestions">
                                            {filteredPlayers.length > 0 ? (
                                              filteredPlayers.map((player) => (
                                                <button
                                                  type="button"
                                                  key={player.id}
                                                  className="tcd-award__suggestion"
                                                  onClick={async () => {
                                                    await assignAwardHandler(player.id, award.id);
                                                    setEditingAwardId(null);
                                                    setTournamentCategory((prev) =>
                                                      prev
                                                        ? {
                                                            ...prev,
                                                            awards: prev.awards.map((a) =>
                                                              a.id === award.id
                                                                ? {
                                                                    ...a,
                                                                    playerName: `${player.firstName} ${player.lastName}`,
                                                                  }
                                                                : a,
                                                            ),
                                                          }
                                                        : prev,
                                                    );
                                                    setSearch((prev) => ({ ...prev, [award.id]: "" }));
                                                  }}
                                                >
                                                  {player.firstName} {player.lastName} – {player.teamName}
                                                </button>
                                              ))
                                            ) : (
                                              <div className="tcd-award__no-results">No players found</div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <p className="tcd-award__player">
                                        {award.playerName.trim() ? award.playerName : "-"}
                                      </p>
                                    )}
                                  </div>
                                  {isSuperAdmin(currentUser) && (
                                    <IconButton
                                      size="sm"
                                      label={isEditing ? "Cancel" : "Edit"}
                                      icon={!isEditing ? <Edit size={16} /> : <X size={16} />}
                                      onClick={() => {
                                        if (isEditing) {
                                          setEditingAwardId(null);
                                        } else {
                                          setEditingAwardId(award.id);
                                          setSearch((prev) => ({
                                            ...prev,
                                            [award.id]: award.playerName.trim() || "",
                                          }));
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  ) : null}
                </div>
              )}
              {activeTab === "winners" && isSuperAdmin(currentUser) && (
                <div className="tcd-winners">
                  {winnersLoading ? (
                    <Loader />
                  ) : winnersData?.data?.prizes?.length ? (
                    <>
                      {winnersData.data.prizes.some((prize) => prize.winner && prize.winner.prizeAmount > 0) && (
                        <Toolbar
                          actions={
                            winnersData.data.prizes.filter(
                              (prize) => !prize.isPaid && prize.winner && prize.winner.prizeAmount > 0,
                            ).length > 0 && (
                              <Button
                                icon={<CheckCircle size={16} />}
                                onClick={openBulkPaymentModal}
                                loading={bulkPaymentLoading}
                              >
                                Mark All as Paid
                              </Button>
                            )
                          }
                        >
                          <Segmented
                            ariaLabel="Filter winners by payment"
                            value={winnersFilter}
                            onChange={(filter) => setWinnersFilter(filter)}
                            options={[
                              { value: "all", label: `All (${winnersData.data.prizes.length})` },
                              {
                                value: "paid",
                                label: `Paid (${winnersData.data.prizes.filter((prize) => prize.isPaid).length})`,
                              },
                              {
                                value: "unpaid",
                                label: `Unpaid (${winnersData.data.prizes.filter((prize) => !prize.isPaid).length})`,
                              },
                            ]}
                          />
                        </Toolbar>
                      )}
                      <div className="tcd-winners__list">
                        {getFilteredWinners()
                          .sort((a, b) => a.rank - b.rank)
                          .map((prize) => (
                            <Card key={prize.id} className="tcd-winner">
                              <div className="tcd-winner__head">
                                <Trophy size={32} className={medalClass(prize.rank)} />
                                <div>
                                  <h3 className="tcd-winner__rank">{getRankName(prize.rank)} Place</h3>
                                  <p className="tcd-winner__prize">
                                    {prize.gift ? prize.gift : `${prize.amount?.toLocaleString()} ${prize.currency}`}
                                  </p>
                                </div>
                              </div>
                              {prize.winner && (
                                <div className="tcd-winner__details">
                                  <h4 className="tcd-winner__team">{prize.winner?.teamName}</h4>
                                  <div className="tcd-winner__lines">
                                    <p>
                                      <strong>Captain:</strong> {prize.winner?.captainName}
                                    </p>
                                    {prize.winner?.captainPhoneNumber && (
                                      <p className="tcd-winner__phone">
                                        <strong>Phone:</strong> {prize.winner?.captainPhoneNumber}
                                        <IconButton
                                          size="sm"
                                          label="Copy phone number"
                                          icon={
                                            copiedPhoneId === prize.id ? (
                                              <Check size={14} className="tcd-icon--ok" />
                                            ) : (
                                              <Copy size={14} />
                                            )
                                          }
                                          onClick={() => copyPhoneToClipboard(prize.winner?.captainPhoneNumber, prize.id)}
                                        />
                                      </p>
                                    )}
                                  </div>
                                  {prize.winner?.prizeAmount > 0 && (
                                    <div className="tcd-winner__payment">
                                      <div className="tcd-winner__lines">
                                        <p className="tcd-winner__status">
                                          <strong>Payment Status:</strong>{" "}
                                          <Tag tone={prize.isPaid ? "ok" : "danger"}>{prize.isPaid ? "Paid" : "Unpaid"}</Tag>
                                        </p>
                                        {prize.isPaid && (
                                          <>
                                            {prize.paymentType && (
                                              <p>
                                                <strong>Type:</strong> {getPaymentTypeDisplay(prize.paymentType)}
                                              </p>
                                            )}
                                            {prize.paymentDate && (
                                              <p>
                                                <strong>Date:</strong> {moment(prize.paymentDate).format("DD MMM YYYY")}
                                              </p>
                                            )}
                                            {prize.paymentReference && (
                                              <p>
                                                <strong>Reference:</strong> {prize.paymentReference}
                                              </p>
                                            )}
                                            {prize.paymentNotes && (
                                              <p>
                                                <strong>Notes:</strong> {prize.paymentNotes}
                                              </p>
                                            )}
                                          </>
                                        )}
                                      </div>
                                      <Button
                                        size="sm"
                                        variant={prize.isPaid ? "ghost" : "primary"}
                                        onClick={() => openPaymentModal(prize.id, prize.winner.teamId, true)}
                                        loading={paymentUpdateLoading === prize.id}
                                      >
                                        {prize.isPaid ? "Update Payment" : "Record Payment"}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </Card>
                          ))}
                      </div>
                    </>
                  ) : (
                    <Card>
                      <EmptyState icon={<Trophy />} title="No winners have been announced yet" />
                    </Card>
                  )}
                </div>
              )}
              {activeTab === "voting" && isSuperAdmin(currentUser) && (
                <div className="tcd-voting">
                  {votingSessions.length > 0 && (
                    <div className="tcd-section-head">
                      <h3 className="tcd-section-head__title">Voting Session</h3>
                      <p className="tcd-section-head__subtitle">Manage the voting session for this category</p>
                    </div>
                  )}

                  {votingLoading ? (
                    <Loader />
                  ) : votingSessions.length > 0 ? (
                    <div className="tcd-voting__list">
                      {votingSessions.map((session) => (
                        <Card
                          key={session.id}
                          className="tcd-session"
                          title="Voting Session"
                          subtitle={
                            <span className="tcd-session__meta">
                              <Tag tone={session.isOpen ? "ok" : "neutral"}>{session.isOpen ? "Open" : "Closed"}</Tag>
                              <Tag tone="brand">
                                {session.eligibleVoterType === 0 ? "All Users" : "Participants Only"}
                              </Tag>
                              <span>Created {new Date(session.createdAt).toLocaleDateString()}</span>
                            </span>
                          }
                          actions={
                            <>
                              <Button
                                size="sm"
                                variant={session.isOpen ? "danger-tint" : "primary"}
                                onClick={() => handleToggleVotingSession(session)}
                              >
                                {session.isOpen ? "Close Voting" : "Open Voting"}
                              </Button>
                              <IconButton
                                label="Edit"
                                icon={<Edit size={16} />}
                                onClick={() => handleEditVotingSession(session)}
                              />
                              <IconButton
                                label="Delete"
                                variant="danger"
                                icon={<Trash size={16} />}
                                onClick={() => handleDeleteVotingSession(session)}
                              />
                            </>
                          }
                        >
                          <DescriptionList
                            columns={2}
                            mobileColumns={2}
                            items={[
                              {
                                label: "Self-voting:",
                                value: (
                                  <Tag tone={session.allowSelfVote ? "ok" : "neutral"}>
                                    {session.allowSelfVote ? "Allowed" : "Not Allowed"}
                                  </Tag>
                                ),
                              },
                              {
                                label: "Same-team voting:",
                                value: (
                                  <Tag tone={session.allowSameTeamVote ? "ok" : "neutral"}>
                                    {session.allowSameTeamVote ? "Allowed" : "Not Allowed"}
                                  </Tag>
                                ),
                              },
                            ]}
                          />

                          {/* Voting Results Section */}
                          <div className="tcd-results">
                            <div className="tcd-results__head">
                              <h4 className="tcd-results__title">Voting Results</h4>
                              <div className="tcd-results__actions">
                                {!session.isOpen && votingResults && votingResults.results.length > 0 && (
                                  <Button size="sm" onClick={handleAssignAwards} loading={resultsLoading}>
                                    Assign Awards
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => getVotingResultsForSession(session.id)}
                                  loading={resultsLoading}
                                >
                                  Refresh
                                </Button>
                              </div>
                            </div>

                            {resultsLoading ? (
                              <Loader label="Loading results..." minHeight={160} />
                            ) : votingResults && votingResults.results.length > 0 ? (
                              <div className="tcd-results__list">
                                {votingResults.results
                                  .reduce(
                                    (acc, result) => {
                                      const existingAward = acc.find((award) => award.awardId === result.awardId);
                                      if (existingAward) {
                                        existingAward.results.push(result);
                                      } else {
                                        acc.push({
                                          awardId: result.awardId,
                                          awardName: result.awardName,
                                          results: [result],
                                        });
                                      }
                                      return acc;
                                    },
                                    [] as {
                                      awardId: string;
                                      awardName: string;
                                      results: typeof votingResults.results;
                                    }[],
                                  )
                                  .map((awardGroup) => (
                                    <div key={awardGroup.awardId} className="tcd-award-result">
                                      <div className="tcd-award-result__head">
                                        <h5 className="tcd-award-result__name">
                                          {awardGroup.awardName
                                            ?.split(" ")
                                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                            .join(" ")}
                                        </h5>
                                        <span className="tcd-award-result__total">
                                          {awardGroup.results.reduce((sum, result) => sum + result.voteCount, 0)} total
                                          votes
                                        </span>
                                      </div>
                                      <div className="tcd-award-result__rows">
                                        {awardGroup.results
                                          .sort((a, b) => b.voteCount - a.voteCount)
                                          .slice(0, 5)
                                          .map((result, index) => (
                                            <div
                                              key={`${result.awardId}-${result.votedPlayerId}`}
                                              className={`tcd-award-result__row${index === 0 ? " is-winner" : ""}`}
                                            >
                                              <div className="tcd-award-result__info">
                                                <span className="tcd-award-result__rank">#{index + 1}</span>
                                                <span className="tcd-award-result__player">
                                                  {result.votedPlayerName
                                                    ?.split(" ")
                                                    .map(
                                                      (word) =>
                                                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
                                                    )
                                                    .join(" ")}
                                                </span>
                                                <span className="tcd-award-result__votes">
                                                  {result.voteCount} vote{result.voteCount !== 1 ? "s" : ""}
                                                </span>
                                              </div>
                                              {index === 0 && <Tag tone="solid-spark">Winner</Tag>}
                                            </div>
                                          ))}
                                        {awardGroup.results.length > 5 && (
                                          <div className="tcd-award-result__more">
                                            +{awardGroup.results.length - 5} more players
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            ) : (
                              <EmptyState compact title="No voting results available yet" />
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card>
                      <EmptyState
                        icon={<Users />}
                        title="No Voting Session"
                        description="Create a voting session to allow participants to vote for awards in this category"
                        action={
                          <Button icon={<Plus size={16} />} onClick={handleCreateVotingSession}>
                            Create Voting Session
                          </Button>
                        }
                      />
                    </Card>
                  )}
                </div>
              )}
              {((activeTab === "teams" &&
                canAccessTeamsTab(currentUser) &&
                tournamentCategory &&
                (tournamentCategory.allowIndividualRegistration !== true || hasDividedTeams) &&
                (tournamentCategory?.teams as ITeamDetail[]).length > 0) ||
                (activeTab === "players" &&
                  canAccessPlayersTab(currentUser) &&
                  (tournamentCategory?.players as IPlayerDetail[]).length > 0) ||
                (activeTab === "winners" && winnersData?.data?.prizes && winnersData.data.prizes.length > 0)) && (
                <Toolbar
                  className="tcd-toolbar"
                  actions={
                    <>
                      {(activeTab === "teams" || activeTab === "players") && (
                        <Button variant="ghost" icon={<Config size={18} />} onClick={() => setIsFilterDialogOpen(true)}>
                          Filter
                          {((activeTab === "players" && playerFiltersApplied > 0) ||
                            (activeTab === "teams" && teamFiltersApplied > 0)) && (
                            <span className="tcd-filter-count">
                              {activeTab === "players"
                                ? playerFiltersApplied
                                : activeTab === "teams"
                                  ? teamFiltersApplied
                                  : 0}
                            </span>
                          )}
                        </Button>
                      )}
                      {activeTab === "teams" &&
                        (tournamentCategory?.allowIndividualRegistration !== true || isBothCategory) &&
                        canAccessTeamsTab(currentUser) && (
                          <Button icon={<Plus size={18} />} onClick={() => setRegisterTeamDialog(true)}>
                            Add Team
                          </Button>
                        )}
                      {activeTab === "players" &&
                        tournamentCategory?.allowIndividualRegistration &&
                        canAccessPlayersTab(currentUser) && (
                          <>
                            <Button icon={<Plus size={18} />} onClick={() => setAddIndividualPlayerDialog(true)}>
                              Add Player
                            </Button>
                            {(isBothCategory
                              ? soloPool.length > 0
                              : tournamentCategory?.players && tournamentCategory.players.length > 0 && !hasDividedTeams) && (
                              <Button variant="tint" icon={<Users size={18} />} onClick={() => setDividePlayersDialog(true)}>
                                Divide into Teams
                              </Button>
                            )}
                          </>
                        )}
                    </>
                  }
                >
                  {checkedItems.length > 0 && (
                    <>
                      <Button variant="ghost" onClick={handleCancel}>
                        Cancel
                      </Button>
                      {activeTab === "players" && (
                        <Button variant="tint" onClick={openContacts}>
                          Add to Contacts ({checkedItems.length})
                        </Button>
                      )}
                      <Button onClick={handleBulkConfirm}>Confirm Selection ({checkedItems.length})</Button>
                    </>
                  )}
                </Toolbar>
              )}
              {/* Show buttons even when no players yet */}
              {activeTab === "players" &&
                tournamentCategory?.allowIndividualRegistration &&
                canAccessPlayersTab(currentUser) &&
                (!tournamentCategory?.players || tournamentCategory.players.length === 0) && (
                  <Toolbar className="tcd-toolbar">
                    <Button icon={<Plus size={18} />} onClick={() => setAddIndividualPlayerDialog(true)}>
                      Add Player
                    </Button>
                  </Toolbar>
                )}
              {activeTab === "players" &&
                canAccessPlayersTab(currentUser) &&
                tournamentCategory?.players &&
                tournamentCategory.players.length > 0 && (
                  <SearchInput
                    className="tcd-search"
                    block
                    placeholder="Search by player name, email, phone number, or team..."
                    value={playerSearchTerm}
                    onChange={(value) => handlePlayerSearch(value)}
                  />
                )}

              {activeTab !== "details" &&
              activeTab !== "arena" &&
              activeTab !== "winners" &&
              ((activeTab === "teams" &&
                canAccessTeamsTab(currentUser) &&
                tournamentCategory &&
                (tournamentCategory.allowIndividualRegistration !== true || hasDividedTeams) &&
                (filteredTeams as ITeamDetail[]).length > 0) ||
                (activeTab === "players" &&
                  canAccessPlayersTab(currentUser) &&
                  (filteredPlayers as IPlayerDetail[]).length > 0)) ? (
                <>
                  {/* Phones: the rows are cards and the table head is hidden, so select-all sits above them */}
                  {activeTab === "players" && (
                    <div className="tcd-select-all">
                      <Checkbox
                        label="Select all players"
                        checked={checkedItems.length === filteredPlayers.length}
                        indeterminate={checkedItems.length > 0 && checkedItems.length < filteredPlayers.length}
                        onChange={() => toggleCheckAll()}
                      />
                    </div>
                  )}
                  <TableWrap className="tcd-table-wrap">
                    <table
                      id={activeTab === "teams" ? "teams-table" : activeTab === "players" ? "players-table" : ""}
                      className={`ui-table ui-table--stack tcd-table ${activeTab === "players" ? "players-table" : "teams-table"}`}
                    >
                      <thead>
                        <tr>
                          {activeTab === "players" && (
                            <th className="tcd-table__check">
                              <RowCheck
                                label="Select all players"
                                checked={checkedItems.length === filteredPlayers.length}
                                indeterminate={checkedItems.length > 0 && checkedItems.length < filteredPlayers.length}
                                onChange={toggleCheckAll}
                              />
                            </th>
                          )}
                          {(activeTab === "teams"
                            ? TEAMS_COLUMNS
                            : activeTab === "players"
                              ? getPlayersColumns(tournamentCategory?.allowIndividualRegistration, hasDividedTeams)
                              : []
                          ).map((item: { label: string; center?: boolean }, id) => (
                            <th style={{ textAlign: item.center ? "center" : "left" }} key={id}>
                              {item.label}
                            </th>
                          ))}
                          {activeTab === "players" &&
                            customFieldColumns.map((field, idx) => (
                              <th key={`custom-${idx}`} style={{ textAlign: "left" }}>
                                {field.fieldName}
                              </th>
                            ))}
                          <th className="tcd-table__menu" />
                        </tr>
                      </thead>
                      <tbody>
                        {(
                          (activeTab === "teams" ? filteredTeams : activeTab === "players" ? filteredPlayers : []) ?? []
                        ).map((row, rowIndex) => {
                          if (activeTab === "teams") {
                            const team = row as ITeamDetail;
                            // Both: a premade team that signed up short waits for a solo player (player row → change team).
                            const shortLabel = isBothCategory
                              ? shortTeamLabel(team.teamName, team.teamMembers.length, tournamentCategory?.teamSize)
                              : null;

                            return (
                              <tr key={rowIndex} className="is-clickable" onClick={() => navigate(`teams/${team.id}`)}>
                                <td className="tcd-table__name ui-table__stack-title">{team.teamName}</td>
                                <td data-label="Captain">{team.captain}</td>
                                <td data-label="Payment status" className="tcd-cell--status">
                                  <Tag tone={statusTone(getPaymentStatusClass(team.paymentStatus))}>{team.paymentStatus}</Tag>
                                </td>
                                <td data-label="# of players">
                                  {shortLabel ? <Tag tone="warn">{shortLabel}</Tag> : team.teamMembers.length}
                                </td>
                                <td data-label="Promo code" className="ui-table__stack-hide">
                                  {team.promoCode ? team.promoCode : "-"}
                                </td>
                                <td data-label="Registration date">{moment(team.createdAt).format("DD MMMM YYYY")}</td>
                                <td className="ui-table__actions tcd-table__menu" onClick={(e) => e.stopPropagation()}>
                                  <Menu
                                    label={`Actions for ${team.teamName}`}
                                    items={[
                                      {
                                        label: "View Team",
                                        icon: <Eye size={16} />,
                                        onClick: () => navigate(`teams/${team.id}`),
                                      },
                                      {
                                        label: "Confirm All Members",
                                        icon: <CheckCircle size={16} className="tcd-icon--ok" />,
                                        visible: isSuperAdmin(currentUser),
                                        onClick: () => {
                                          setTeamToConfirmMembers(team);
                                          setConfirmAllMembersDialog(true);
                                        },
                                      },
                                      {
                                        label: "Delete",
                                        icon: <Trash size={16} />,
                                        tone: "danger",
                                        onClick: () => {
                                          setTeamToDelete(team);
                                          setDeleteTeamDialog(true);
                                        },
                                      },
                                    ]}
                                  />
                                </td>
                              </tr>
                            );
                          }

                          if (activeTab === "players") {
                            const player = row as IPlayerDetail;
                            const isRegistered = !!player.id && player.id !== "00000000-0000-0000-0000-000000000000";
                            const registrationClass = player.registrationStatus.includes("Pending")
                              ? "pending"
                              : player.registrationStatus === "Confirmed"
                                ? "confirmed"
                                : "canceled";
                            const paymentClass =
                              player.paymentStatus.includes("Pending") || player.paymentStatus.includes("Cash")
                                ? "pending"
                                : player.paymentStatus === "Paid"
                                  ? "confirmed"
                                  : "canceled";

                            return (
                              <tr
                                className={`is-clickable${checkedItems.includes(row.id) ? " is-selected" : ""}`}
                                onClick={() => (checkedItems.length === 0 ? setActivePlayer(player) : toggleCheck(row.id))}
                              >
                                <td className="tcd-table__check ui-table__check" onClick={(evt) => evt.stopPropagation()}>
                                  <RowCheck
                                    label={`Select ${player.firstName} ${player.lastName ?? ""}`.trim()}
                                    checked={checkedItems.includes(row.id)}
                                    onChange={() => toggleCheck(row.id)}
                                  />
                                </td>
                                <td className="tcd-table__name ui-table__stack-title">
                                  <div className="tcd-player-name">
                                    <span className="tcd-player-name__text">
                                      {player.firstName} {player.lastName ?? ""}
                                    </span>
                                    {player.isCaptain && <LetterCSquare size={18} className="tcd-player-name__captain" />}
                                    {!isRegistered && <Tag tone="neutral">Non-registered</Tag>}
                                  </div>
                                </td>
                                <td className="ui-table__stack-hide">{isRegistered ? player.nationality || "-" : "-"}</td>
                                {tournamentCategory?.allowIndividualRegistration && (
                                  <td className="tcd-capitalize ui-table__stack-hide">
                                    {isRegistered ? player.gender || "-" : "-"}
                                  </td>
                                )}
                                {tournamentCategory?.allowIndividualRegistration && (
                                  <td className="ui-table__stack-hide">{player.promoCode ?? "-"}</td>
                                )}
                                {tournamentCategory?.allowIndividualRegistration && (
                                  <td data-label="Total">{getPlayerTotal(player, tournamentPromoCodes)}</td>
                                )}
                                <td className="ui-table__stack-hide">{isRegistered ? player.email || "-" : "-"}</td>
                                <td data-label="Phone Number">{isRegistered ? player.phoneNumber || "-" : "-"}</td>
                                <td data-label="Registration status" className="tcd-cell--status">
                                  <Tag tone={statusTone(registrationClass)}>{player.registrationStatus}</Tag>
                                </td>
                                <td data-label="Payment status" className="tcd-cell--status">
                                  <Tag tone={statusTone(paymentClass)}>{player.paymentStatus}</Tag>
                                </td>
                                <td data-label="Payment method">
                                  {player.paymentMethod
                                    ? player.paymentMethod === "fawry"
                                      ? "Fawry"
                                      : player.paymentMethod === "instapay"
                                        ? "InstaPay"
                                        : player.paymentMethod === "balance"
                                          ? "Balance"
                                          : player.paymentMethod
                                    : "-"}
                                </td>
                                {(!tournamentCategory?.allowIndividualRegistration || hasDividedTeams) && (
                                  <td data-label="Team">
                                    {player.teamName === "__INDIVIDUAL_REGISTRATION__" ? "Unassigned" : player.teamName}
                                  </td>
                                )}
                                <td className="ui-table__stack-hide">
                                  {player.fee ? `${player.fee.description} - ${player.fee.amount} ${player.fee.currency}` : "-"}
                                </td>
                                <td className="ui-table__stack-hide">{player.notes ?? "-"}</td>
                                {customFieldColumns.map((field, idx) => (
                                  <td key={`custom-value-${idx}`} className="ui-table__stack-hide">
                                    {getPlayerCustomFieldValue(player, field.fieldKey)}
                                  </td>
                                ))}
                                <td className="ui-table__actions tcd-table__menu" onClick={(e) => e.stopPropagation()}>
                                  <Menu
                                    label={`Actions for ${player.firstName} ${player.lastName ?? ""}`.trim()}
                                    items={[
                                      {
                                        label: "Edit",
                                        icon: <Pencil size={16} />,
                                        onClick: () => setActivePlayer(player),
                                      },
                                      {
                                        label: "View Profile",
                                        icon: <Eye size={16} />,
                                        onClick: () => {
                                          const currentUrl = location.pathname + location.search;
                                          navigate(
                                            `/dashboard/players/${player.id}?goTo=category&playerId=${
                                              player.id
                                            }&returnUrl=${encodeURIComponent(currentUrl)}`,
                                          );
                                        },
                                      },
                                      {
                                        label: "Whatsapp",
                                        icon: <Telephone size={16} />,
                                        visible: !!player.phoneNumber,
                                        onClick: () => window.open(`https://wa.me/${player.phoneNumber}`, "_blank"),
                                      },
                                      {
                                        label: "Telegram",
                                        icon: <BrandTelegram size={16} />,
                                        visible: !!player.phoneNumber,
                                        onClick: () => window.open(`https://t.me/${player.phoneNumber}`, "_blank"),
                                      },
                                      {
                                        label: "Email",
                                        icon: <Envelope size={16} />,
                                        onClick: () => {
                                          window.location.href = `mailto:${player.email}`;
                                        },
                                      },
                                      {
                                        label: "Add to Contacts",
                                        icon: <Users size={16} className="tcd-icon--info" />,
                                        onClick: () => addPlayerToContacts(player),
                                      },
                                      {
                                        label: "Delete",
                                        icon: <Trash size={16} />,
                                        tone: "danger",
                                        onClick: () => {
                                          setDeletePlayerDialog(true);
                                          setPlayerToDelete(player);
                                        },
                                      },
                                    ]}
                                  />
                                </td>
                              </tr>
                            );
                          }
                        })}
                      </tbody>
                    </table>
                  </TableWrap>
                </>
              ) : (activeTab === "players" && canAccessPlayersTab(currentUser)) ||
                (activeTab === "teams" && canAccessTeamsTab(currentUser)) ? (
                <Card>
                  <EmptyState
                    icon={<Users />}
                    title={`No ${activeTab}`}
                    description={
                      activeTab === "players" && playerSearchTerm
                        ? `No players found matching "${playerSearchTerm}"`
                        : (activeTab === "players" && playerFiltersApplied > 0) ||
                            (activeTab === "teams" && teamFiltersApplied > 0)
                          ? `There are no ${activeTab} matching the filters`
                          : `This category currently has no registered ${activeTab}`
                    }
                    action={
                      activeTab === "teams" &&
                      (tournamentCategory?.allowIndividualRegistration !== true || isBothCategory) &&
                      canAccessTeamsTab(currentUser) ? (
                        <Button icon={<Plus size={18} />} onClick={() => setRegisterTeamDialog(true)}>
                          Add Team
                        </Button>
                      ) : undefined
                    }
                  />
                </Card>
              ) : null}

              {activeTab === "arena" &&
                canAccessArena(currentUser) &&
                (tournamentCategory?.allowIndividualRegistration !== true ||
                  hasDividedTeams ||
                  isAmericanoCategory ||
                  isBothCategory) &&
                tournamentCategory?.customFormatBlocks &&
                tournamentCategory?.customFormatBlocks.length > 0 && (
                  <div className="tcd-arena">
                    <div className="tcd-arena__side">
                      <Select
                        block
                        aria-label="Stage"
                        value={selectedBlockId}
                        onChange={(e) => setSelectedBlockId(e.target.value)}
                      >
                        {tournamentCategory?.customFormatBlocks
                          ?.sort((a, b) => a.orderIndex - b.orderIndex)
                          .map((block) => (
                            <option key={block.id} value={block.id}>
                              Stage {block.orderIndex} - {block?.aliasName || block.blockType}
                            </option>
                          ))}
                      </Select>
                      <Card title="Stage Details">
                        <DescriptionList
                          columns={2}
                          mobileColumns={2}
                          items={[
                            { label: "Format", value: selectedBlock?.blockType },
                            ...(selectedBlock?.leagueRules
                              ? [
                                  { label: "Encounters", value: selectedBlock?.leagueRules?.encounters },
                                  {
                                    label: "Criteria",
                                    value:
                                      selectedBlock?.leagueRules?.criteria === "total" ? "Total Points" : "Direct Matches",
                                  },
                                  { label: "Points for Win", value: selectedBlock?.leagueRules?.pointsForWin },
                                  { label: "Points for Loss", value: selectedBlock?.leagueRules?.pointsForLoss },
                                ]
                              : []),
                            ...(selectedBlock?.knockoutRules
                              ? [
                                  {
                                    label: "Mode",
                                    value:
                                      selectedBlock?.knockoutRules?.mode === "single"
                                        ? "Single Elimination "
                                        : "Double Elimination",
                                  },
                                  {
                                    label: "3rd Place Match",
                                    value: selectedBlock?.knockoutRules?.hasThirdPlaceMatch ? "Yes" : "No",
                                  },
                                ]
                              : []),
                            ...(selectedBlock?.americanoRules
                              ? [
                                  { label: "Points per Match", value: selectedBlock?.americanoRules?.pointsPerMatch },
                                  { label: "Rounds", value: formatPlannedRounds(selectedBlock?.americanoRules?.roundsCount) },
                                  { label: "Unit Size", value: selectedBlock?.americanoRules?.unitSize },
                                  ...(selectedBlock?.blockType === "Roulette"
                                    ? [
                                        {
                                          label: "Players per Side",
                                          value: playersPerSideFor(
                                            selectedBlock?.americanoRules?.unitsPerSide,
                                            selectedBlock?.americanoRules?.unitSize,
                                          ),
                                        },
                                      ]
                                    : []),
                                  // Mexicano and Roulette never pair by gender.
                                  ...(selectedBlock?.blockType === "Americano"
                                    ? [
                                        {
                                          label: "Mixed Pairing",
                                          value: selectedBlock?.americanoRules?.mixedPairing ? "Yes" : "No",
                                        },
                                      ]
                                    : []),
                                ]
                              : []),
                          ]}
                        />
                      </Card>
                      <Button
                        variant="tint"
                        block
                        iconRight={<ArrowRight size={18} />}
                        onClick={() =>
                          navigate(`/dashboard/tournaments/${id}/${tournamentCategory?.id}/stages/${selectedBlockId}`)
                        }
                      >
                        Stage overview
                      </Button>
                    </div>
                    <Card className="tcd-arena__matches" padding="none">
                      {tournamentCategory.teams.length > 0 ? (
                        matches?.length > 0 ? (
                          <>
                            <div className="tcd-matches__head">
                              <Segmented
                                ariaLabel="Matches"
                                value={activeMatchTab}
                                onChange={(matchTab) => {
                                  setActiveMatchTab(matchTab);
                                  searchParams.set("matchTab", matchTab);
                                  setSearchParams(searchParams);
                                }}
                                options={[
                                  { value: "upcoming", label: "Upcoming Matches" },
                                  { value: "latest", label: "Latest Results" },
                                ]}
                              />
                              {hasEmptyTeamsInFirstRound() && canCompleteBracket(currentUser) && (
                                <Button size="sm" icon={<Trophy size={16} />} onClick={handleCompleteBracketClick}>
                                  Complete Bracket
                                </Button>
                              )}
                            </div>
                            {matchesLoading ? (
                              <Loader />
                            ) : (
                              <div className="tcd-matches__list">
                                {(activeMatchTab === "latest"
                                  ? matches.filter((match) => match.isCompleted)
                                  : activeMatchTab === "upcoming"
                                    ? matches.filter((x) => !x.isCompleted)
                                    : []
                                ).length > 0 ? (
                                  (activeMatchTab === "latest"
                                    ? matches.filter((match) => match.isCompleted)
                                    : activeMatchTab === "upcoming"
                                      ? matches.filter((x) => !x.isCompleted)
                                      : []
                                  )
                                    .sort((a, b) => {
                                      if (activeMatchTab === "latest") {
                                        if (!a.winnerTeamId && !b.winnerTeamId) return 0;
                                        if (!a.winnerTeamId) return 1;
                                        if (!b.winnerTeamId) return -1;
                                        return moment(b.startTime).diff(moment(a.startTime));
                                      } else {
                                        return moment(a.startTime).diff(moment(b.startTime));
                                      }
                                    })
                                    .map((match) => <MatchCard match={match} />)
                                ) : (
                                  <EmptyState
                                    compact
                                    className="tcd-matches__empty"
                                    icon={<Calendar />}
                                    title="No matches"
                                    description={
                                      activeMatchTab === "upcoming"
                                        ? "There are no upcoming matches"
                                        : "There are no match results yet"
                                    }
                                  />
                                )}
                              </div>
                            )}
                          </>
                        ) : tournamentCategory.customFormatBlocks
                            .filter(
                              (x) =>
                                x.orderIndex <
                                tournamentCategory.customFormatBlocks.find((block) => block.id === selectedBlockId)!
                                  ?.orderIndex,
                            )
                            .findIndex((item) => !item.hasEnded) !== -1 ? (
                          <EmptyState
                            icon={<GitMerge />}
                            title="Waiting for other stages"
                            description="This stage's scheduling is dependent on one or multiple other stage(s). You will be able to schedule this stage's matches once its dependents are complete."
                            action={
                              canCreateSchedule(currentUser) && (
                                <Button
                                  iconRight={<ArrowRight size={18} />}
                                  onClick={() =>
                                    navigate(
                                      `/dashboard/tournaments/${id}/${tournamentCategory?.id}/stages/${selectedBlockId}/create-schedule?goTo=category`,
                                    )
                                  }
                                >
                                  Create Schedule Anyway
                                </Button>
                              )
                            }
                          />
                        ) : (
                          <EmptyState
                            icon={<Calendar />}
                            title={`${tournamentCategory.allowIndividualRegistration ? "Players" : "Teams"} registered`}
                            description={`${
                              tournamentCategory.allowIndividualRegistration ? "Players" : "Teams"
                            } have registered for this category. This category is ready for match scheduling`}
                            action={
                              canCreateSchedule(currentUser) && (
                                <Button
                                  iconRight={<ArrowRight size={18} />}
                                  onClick={() =>
                                    navigate(
                                      `/dashboard/tournaments/${id}/${tournamentCategory?.id}/stages/${selectedBlockId}/create-schedule?goTo=category`,
                                    )
                                  }
                                >
                                  Create Schedule
                                </Button>
                              )
                            }
                          />
                        )
                      ) : (
                        <EmptyState
                          icon={<Users />}
                          title="Waiting for registrations"
                          description="Waiting for teams to register for this category"
                        />
                      )}
                    </Card>
                  </div>
                )}
          </div>
        </>
      )}
      {activePlayer && (
        <PlayerStatusDialog
          item={activePlayer}
          isOpen={!!activePlayer}
          onClose={() => setActivePlayer(undefined)}
          teamId={activePlayer.teamId}
          fees={tournamentCategory?.fees}
          promoCodes={tournamentPromoCodes}
          teams={
            hasDividedTeams
              ? (tournamentCategory?.teams as any[])?.map((t: any) => ({ id: t.id, teamName: t.teamName }))
              : undefined
          }
          hasDividedTeams={hasDividedTeams}
          onConfirm={() => {
            setActivePlayer(undefined);
            setLoading(true);
            getTournamentData();
          }}
        />
      )}
      <FilterDialog
        isOpen={isFilterDialogOpen}
        onApply={handleFilterApply}
        onClose={() => setIsFilterDialogOpen(false)}
        filters={activeTab === "players" ? playerFilters : activeTab === "teams" ? teamFilters : []}
      />
      <DeleteCategoryDialog
        isOpen={deleteDialog}
        onClose={handleCloseDeleteDialog}
        categoryId={tournamentCategory?.id!}
        categoryName={tournamentCategory?.categoryName!}
      />
      <EditCategoryDialog
        isOpen={editDialog}
        onClose={(update) => {
          if (update) getTournamentData();
          setEditDialog(false);
        }}
        category={tournamentCategory!}
        mode={editMode}
        tournament={
          tournamentCategory?.tournamentStartDate && tournamentCategory?.tournamentEndDate
            ? { startDate: tournamentCategory.tournamentStartDate, endDate: tournamentCategory.tournamentEndDate }
            : undefined
        }
      />
      <DeletePlayerDialog
        isOpen={deletePlayerDialog}
        onClose={handleCloseDeletePlayerDialog}
        onDelete={handleDeleteTeamMember}
        playerName={`${playerToDelete?.firstName} ${playerToDelete?.lastName ?? ""}`}
      />
      <DeleteTeamDialog
        isOpen={deleteTeamDialog}
        onClose={handleCloseDeleteTeamDialog}
        onDelete={handleDeleteTeam}
        teamName={teamToDelete?.teamName!}
      />
      <AddIndividualPlayerDialog
        isOpen={addIndividualPlayerDialog}
        onClose={() => setAddIndividualPlayerDialog(false)}
        onConfirm={() => {
          setLoading(true);
          getTournamentData();
          dispatch(setGeneralAlertMessage({ message: "Player added successfully", type: "success" }));
        }}
        tournamentCategoryId={tournamentCategoryId!}
        currentPlayers={tournamentCategory?.players || []}
      />
      <DividePlayersDialog
        isOpen={dividePlayersDialog}
        onClose={() => setDividePlayersDialog(false)}
        tournamentCategoryMappingId={tournamentCategoryId!}
        players={soloPool.map((p) => ({
          id: p.playerId || p.id,
          firstName: p.firstName,
          lastName: p.lastName,
          gender: p.gender,
          nationality: p.nationality,
        }))}
        onSave={() => {
          setLoading(true);
          getTournamentData();
          dispatch(setGeneralAlertMessage({ message: "Players divided into teams successfully", type: "success" }));
        }}
        defaultTeamSize={tournamentCategory?.teamSize ?? 2}
      />
      <RegisterTeamDialog
        isOpen={registerTeamDialog}
        onClose={() => setRegisterTeamDialog(false)}
        onConfirm={() => {
          setLoading(true);
          getTournamentData();
          dispatch(setGeneralAlertMessage({ message: "Team added successfully", type: "success" }));
        }}
        tournamentCategoryId={tournamentCategoryId!}
        fees={tournamentCategory?.fees || []}
        // A Both category's maxPlayers is its player capacity; its teams have from the minimum up to the team size.
        maxPlayers={(isBothCategory ? tournamentCategory?.teamSize : tournamentCategory?.maxPlayers) || 0}
        teamSize={isBothCategory ? tournamentCategory?.teamSize ?? undefined : undefined}
        minSize={isBothCategory ? tournamentCategory?.teamMinSize ?? undefined : undefined}
        registrationGate={tournamentCategory?.registrationGate}
      />
      <ConfirmAllTeamMembersDialog
        isOpen={confirmAllMembersDialog}
        onClose={() => {
          setConfirmAllMembersDialog(false);
          setTeamToConfirmMembers(null);
        }}
        onConfirm={handleConfirmAllTeamMembers}
        teamName={teamToConfirmMembers?.teamName || ""}
        memberCount={teamToConfirmMembers?.teamMembers.length || 0}
      />

      {/* Payment Modal */}
      {paymentModalOpen && (
        <Dialog
          open={paymentModalOpen}
          onClose={closePaymentModal}
          title={selectedPrize?.isPaid ? "Update Payment Details" : "Record Payment"}
          busy={paymentUpdateLoading === selectedPrize?.prizeId}
          bodyClassName="tcd-payment-form"
          footer={
            <>
              <Button variant="ghost" onClick={closePaymentModal}>
                Cancel
              </Button>
              <Button onClick={updatePaymentStatus} loading={paymentUpdateLoading === selectedPrize?.prizeId}>
                {paymentUpdateLoading === selectedPrize?.prizeId
                  ? "Updating..."
                  : selectedPrize?.isPaid
                    ? "Update Payment"
                    : "Record Payment"}
              </Button>
            </>
          }
        >
          <Field label="Payment Type" required>
            {(fieldId) => (
              <Select
                id={fieldId}
                block
                value={paymentForm.paymentType}
                onChange={(e) =>
                  setPaymentForm((prev) => ({ ...prev, paymentType: Number(e.target.value) as PaymentType }))
                }
              >
                <option value={PaymentType.BankTransfer}>Bank Transfer</option>
                <option value={PaymentType.Cheque}>Cheque</option>
                <option value={PaymentType.Cash}>Cash</option>
                <option value={PaymentType.Instapay}>Instapay</option>
              </Select>
            )}
          </Field>
          <Field label="Payment Reference">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                placeholder="Enter payment reference number"
                value={paymentForm.paymentReference}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
              />
            )}
          </Field>
          <Field label="Notes">
            {(fieldId) => (
              <Textarea
                id={fieldId}
                placeholder="Enter any additional notes"
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            )}
          </Field>
        </Dialog>
      )}

      {/* Bulk Payment Modal */}
      {bulkPaymentModalOpen && (
        <Dialog
          open={bulkPaymentModalOpen}
          onClose={closeBulkPaymentModal}
          title="Bulk Payment Update"
          busy={bulkPaymentLoading}
          bodyClassName="tcd-payment-form"
          footer={
            <>
              <Button variant="ghost" onClick={closeBulkPaymentModal}>
                Cancel
              </Button>
              <Button onClick={updateBulkPaymentStatus} loading={bulkPaymentLoading}>
                {bulkPaymentLoading ? "Updating..." : "Mark All as Paid"}
              </Button>
            </>
          }
        >
          <Alert tone="info">
            This will mark all{" "}
            <strong>
              {
                winnersData?.data?.prizes?.filter(
                  (prize) => !prize.isPaid && prize.winner && prize.winner.prizeAmount > 0,
                ).length
              }
            </strong>{" "}
            unpaid prizes as paid with the same payment details.
          </Alert>
          <Field label="Payment Type" required>
            {(fieldId) => (
              <Select
                id={fieldId}
                block
                value={bulkPaymentForm.paymentType}
                onChange={(e) =>
                  setBulkPaymentForm((prev) => ({ ...prev, paymentType: Number(e.target.value) as PaymentType }))
                }
              >
                <option value={PaymentType.BankTransfer}>Bank Transfer</option>
                <option value={PaymentType.Cheque}>Cheque</option>
                <option value={PaymentType.Cash}>Cash</option>
                <option value={PaymentType.Instapay}>Instapay</option>
              </Select>
            )}
          </Field>
          <Field label="Payment Reference">
            {(fieldId) => (
              <Input
                id={fieldId}
                type="text"
                placeholder="Enter payment reference number"
                value={bulkPaymentForm.paymentReference}
                onChange={(e) => setBulkPaymentForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
              />
            )}
          </Field>
          <Field label="Notes">
            {(fieldId) => (
              <Textarea
                id={fieldId}
                placeholder="Enter any additional notes"
                value={bulkPaymentForm.notes}
                onChange={(e) => setBulkPaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            )}
          </Field>
        </Dialog>
      )}

      {/* Voting Dialogs */}
      <CreateVotingSessionDialog
        tournamentId={id!}
        tournamentCategoryId={tournamentCategoryId}
        isOpen={createVotingDialog}
        awards={tournamentCategory?.awards}
        categoryId={tournamentCategory?.id}
        onClose={(update) => {
          setCreateVotingDialog(false);
          if (update) {
            getVotingSessions();
            getTournamentData(); // Refresh category data to get updated award includedInVoting values
            dispatch(setGeneralAlertMessage({ message: "Voting session created successfully", type: "success" }));
          }
        }}
      />

      {selectedVotingSession && (
        <>
          <EditVotingSessionDialog
            votingSession={selectedVotingSession}
            isOpen={editVotingDialog}
            awards={tournamentCategory?.awards}
            categoryId={tournamentCategory?.id}
            onClose={(update) => {
              setEditVotingDialog(false);
              setSelectedVotingSession(null);
              if (update) {
                getVotingSessions();
                getTournamentData(); // Refresh category data to get updated award includedInVoting values
                dispatch(setGeneralAlertMessage({ message: "Voting session updated successfully", type: "success" }));
              }
            }}
          />

          <DeleteVotingSessionDialog
            votingSession={selectedVotingSession}
            isOpen={deleteVotingDialog}
            onClose={(update) => {
              setDeleteVotingDialog(false);
              setSelectedVotingSession(null);
              if (update) {
                getVotingSessions();
                dispatch(setGeneralAlertMessage({ message: "Voting session deleted successfully", type: "success" }));
              }
            }}
          />
        </>
      )}
      {selectedStage && (
        <CompleteBracketDialog
          stage={selectedStage}
          isOpen={completeBracketDialog}
          isLoadingStageDetails={stageDetailsLoading}
          onClose={(update) => {
            setCompleteBracketDialog(false);
            setSelectedStage(null);
            setStageDetailsLoading(false);
            if (update) {
              getFormatMatches(selectedBlockId);
            }
          }}
        />
      )}
    </div>
  );
};

export default TournamentCategoryDetail;
